import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  LogOut, Download, Trash2, Search, Music, Users, CalendarClock, Share2, Mail, Phone, Instagram, Facebook, Twitter,
  Eye, UserCheck, Ticket as TicketIcon, DollarSign, TrendingUp, Flame, Trophy, Upload, Send, LayoutDashboard, MapPin, Globe, Monitor, Smartphone, Zap, MessageSquare, Armchair, ScanLine, Wind, Percent, Power, Sparkles, UsersRound, TicketPercent,
} from "lucide-react";
import { api, API, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const StatCard = ({ icon: Icon, label, value }) => (
  <div className="border border-white/10 bg-black/40 p-6 hover:border-[#D4AF37]/40 transition-colors duration-300">
    <div className="flex items-center justify-between">
      <span className="text-xs uppercase tracking-[0.2em] text-[#A19D94]">{label}</span>
      <Icon className="w-4 h-4 text-[#D4AF37]" />
    </div>
    <div className="font-serif-display text-4xl text-[#FDFBF7] mt-3">{value}</div>
  </div>
);

const Social = ({ icon: Icon, value, symbol }) =>
  value ? (
    <span className="inline-flex items-center gap-1 text-xs text-[#A19D94] mr-3">
      {Icon ? <Icon className="w-3.5 h-3.5 text-[#8C7324]" /> : <span className="text-[#8C7324]">{symbol}</span>}
      {value}
    </span>
  ) : null;

const SectionHead = ({ icon: Icon, title, sub }) => (
  <div className="mb-8">
    <div className="flex items-center gap-3">
      <Icon className="w-4 h-4 text-[#D4AF37]" />
      <span className="text-xs uppercase tracking-[0.25em] text-[#D4AF37]">{title}</span>
    </div>
    {sub && <p className="text-sm text-[#A19D94] mt-2">{sub}</p>}
  </div>
);

const TABS = [
  { key: "overview", label: "Overview", Icon: LayoutDashboard },
  { key: "events", label: "Events", Icon: TicketIcon },
  { key: "requests", label: "Song Requests", Icon: Music },
  { key: "early", label: "Early Access", Icon: Zap },
  { key: "codes", label: "Codes", Icon: Percent },
  { key: "contest", label: "Costume Contest", Icon: Trophy },
  { key: "blast", label: "Email Blast", Icon: Send },
  { key: "sms", label: "SMS Blast", Icon: MessageSquare },
  { key: "sections", label: "Sections", Icon: Armchair },
  { key: "hookah", label: "Hookah", Icon: Wind },
  { key: "door", label: "Door", Icon: ScanLine },
  { key: "guests", label: "Guests", Icon: UserCheck },
  { key: "customers", label: "Customers", Icon: Search },
];

const STAFF_TABS = ["door", "guests"];

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const isStaff = user?.role === "staff";
  const visibleTabs = isStaff ? TABS.filter((t) => STAFF_TABS.includes(t.key)) : TABS;
  const [tab, setTab] = useState(isStaff ? "door" : "overview");
  const [staffBoot, setStaffBoot] = useState(null);
  const [guestQuery, setGuestQuery] = useState("");
  const [guestResults, setGuestResults] = useState(null);
  const [guestBusy, setGuestBusy] = useState(false);
  const [qrBusy, setQrBusy] = useState("");
  const [qrConfirm, setQrConfirm] = useState(false);
  const [stripeStats, setStripeStats] = useState(null);
  const [ticketOrders, setTicketOrders] = useState([]);

  const sendQrAll = async () => {
    setQrBusy("all");
    try {
      const { data } = await api.post("/tickets/send-qrs");
      toast.success(`QR passes emailed to ${data.sent} ticket holder${data.sent === 1 ? "" : "s"}${data.failed ? ` (${data.failed} failed)` : ""}.`);
      setQrConfirm(false);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not send QR passes.");
    } finally {
      setQrBusy("");
    }
  };

  const sendQrOne = async (sessionId) => {
    setQrBusy(sessionId);
    try {
      await api.post(`/tickets/${sessionId}/send-qr`);
      toast.success("QR pass emailed.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not send the QR pass.");
    } finally {
      setQrBusy("");
    }
  };

  const searchGuests = async (query) => {
    setGuestQuery(query);
    if (query.trim().length < 2) { setGuestResults(null); return; }
    setGuestBusy(true);
    try {
      const { data } = await api.get(`/staff/lookup?q=${encodeURIComponent(query.trim())}`);
      setGuestResults(data.results);
    } catch {
      toast.error("Lookup failed — try again.");
    } finally {
      setGuestBusy(false);
    }
  };
  const [subs, setSubs] = useState([]);
  const [stats, setStats] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [ticketForms, setTicketForms] = useState({});
  const [savingEvent, setSavingEvent] = useState("");
  const [contest, setContest] = useState([]);
  const [early, setEarly] = useState([]);
  const [eventList, setEventList] = useState([]);
  const [bannerBusy, setBannerBusy] = useState("");
  const [addOpen, setAddOpen] = useState(false);
  const [addBusy, setAddBusy] = useState(false);
  const [newEvent, setNewEvent] = useState({ title: "", date_label: "", event_datetime: "", location: "", tagline: "", blurb: "" });
  const [siteLive, setSiteLive] = useState(true);
  const [siteBusy, setSiteBusy] = useState(false);
  const [ticketsMode, setTicketsMode] = useState("off");
  const [modeBusy, setModeBusy] = useState(false);
  const [codesBusy, setCodesBusy] = useState(false);
  const [codesAudience, setCodesAudience] = useState("early");
  const [codesDiscount, setCodesDiscount] = useState("0");
  const [goldenList, setGoldenList] = useState([]);
  const [goldenForm, setGoldenForm] = useState({ claim_deadline: "Thursday, November 27", redemption_deadline: "Wednesday, December 31", package: "a complimentary manicure and pedicure", exclusions: "Gratuity not included" });
  const [goldenBusy, setGoldenBusy] = useState(false);
  const [goldenConfirm, setGoldenConfirm] = useState(false);
  const [goldenMode, setGoldenMode] = useState("random"); // random | pick
  const [goldenEligible, setGoldenEligible] = useState([]);
  const [goldenPicks, setGoldenPicks] = useState([]);
  const [earlyCodeUi, setEarlyCodeUi] = useState(true);
  const [custQ, setCustQ] = useState("");
  const [custResults, setCustResults] = useState([]);
  const [custBusy, setCustBusy] = useState(false);
  const [custProfile, setCustProfile] = useState(null);
  const [custProfileBusy, setCustProfileBusy] = useState(false);
  const [custEdit, setCustEdit] = useState({ name: "", phone: "" });  const [codesConfirm, setCodesConfirm] = useState(false);
  const [priceForms, setPriceForms] = useState({ oct4: "10", halloween: "25", bundle: "30" });
  const [bundleEnabled, setBundleEnabled] = useState(true);
  const [savingPrice, setSavingPrice] = useState("");
  const [taxMode, setTaxMode] = useState("auto");
  const [taxRate, setTaxRate] = useState("");
  const [savingTax, setSavingTax] = useState(false);

  const [promoData, setPromoData] = useState(null);
  const [promoBusy, setPromoBusy] = useState("");
  const [promoForm, setPromoForm] = useState({ code: "", kind: "percent", value: "", uses: "single", note: "" });

  const togglePromos = async () => {
    setPromoBusy("master");
    try {
      const { data } = await api.post("/promo-codes/toggle", { enabled: !promoData.enabled });
      setPromoData((d) => ({ ...d, enabled: data.enabled }));
      toast.success(data.enabled
        ? "Discount codes are ON — active codes work at checkout."
        : "Discount codes are OFF — every code is now rejected at checkout.");
    } catch {
      toast.error("Could not flip the code switch.");
    } finally {
      setPromoBusy("");
    }
  };

  const createPromo = async () => {
    const value = parseFloat(promoForm.value);
    if (isNaN(value) || value <= 0) {
      toast.error(promoForm.kind === "percent" ? "Enter a percent off (1–90)." : "Enter a dollar amount off (e.g. 5).");
      return;
    }
    setPromoBusy("create");
    try {
      const { data } = await api.post("/promo-codes", {
        code: promoForm.code.trim(),
        kind: promoForm.kind,
        percent_off: promoForm.kind === "percent" ? Math.round(value) : 0,
        amount_off: promoForm.kind === "amount" ? Math.round(value * 100) : 0,
        max_redemptions: promoForm.uses === "single" ? 1 : 0,
        note: promoForm.note.trim(),
      });
      setPromoData((d) => ({ ...d, codes: [data, ...d.codes] }));
      setPromoForm({ code: "", kind: "percent", value: "", uses: "single", note: "" });
      toast.success(`Code ${data.code} is live — ${data.kind === "percent" ? `${data.percent_off}% off` : `$${(data.amount_off / 100).toFixed(0)} off`}, ${data.max_redemptions ? "single use" : "unlimited uses"}.`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not create the code.");
    } finally {
      setPromoBusy("");
    }
  };

  const togglePromoCode = async (c) => {
    setPromoBusy(c.id);
    try {
      const { data } = await api.patch(`/promo-codes/${c.id}`, { active: !c.active });
      setPromoData((d) => ({ ...d, codes: d.codes.map((x) => x.id === c.id ? { ...x, active: data.active } : x) }));
      toast.success(`${c.code} ${data.active ? "is active again" : "is off — it will be rejected at checkout"}.`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not update the code.");
    } finally {
      setPromoBusy("");
    }
  };

  const saveTax = async () => {
    const pct = parseFloat(taxRate);
    if (taxMode === "flat" && (isNaN(pct) || pct <= 0 || pct > 30)) {
      toast.error("Enter the confirmed combined rate (e.g. 9.679).");
      return;
    }
    setSavingTax(true);
    try {
      const { data } = await api.post("/tax/config", { mode: taxMode, flat_percent: taxMode === "flat" ? pct : null });
      setTaxMode(data.mode);
      setTaxRate(data.flat_percent ? String(data.flat_percent) : "");
      toast.success(data.mode === "flat"
        ? `Flat ${data.flat_percent}% venue tax now applies to every ticket.`
        : "Tax is address-based — Stripe calculates each buyer's rate.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not save tax settings.");
    } finally {
      setSavingTax(false);
    }
  };

  const savePrice = async (product) => {
    const dollars = parseFloat(priceForms[product]);
    if (isNaN(dollars) || dollars < 1 || dollars > 1000) {
      toast.error("Enter a valid price in dollars (1–1000).");
      return;
    }
    setSavingPrice(product);
    try {
      const { data } = await api.post("/prices", { product, amount_cents: Math.round(dollars * 100) });
      setPriceForms((f) => ({ ...f, [product]: String(data.prices[product] / 100) }));
      toast.success(`Price updated — new checkouts charge $${(data.prices[product] / 100).toFixed(0)}.`);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not update price.");
    } finally {
      setSavingPrice("");
    }
  };

  const setMode = async (mode) => {
    setModeBusy(true);
    try {
      await api.post("/tickets/mode", { mode });
      setTicketsMode(mode);
      toast.success(mode === "off" ? "Ticket sales are OFF — early access capture only."
        : mode === "early" ? "Early access window ON — only valid codes can buy."
        : "Ticket sales are PUBLIC — anyone can buy.");
    } catch {
      toast.error("Could not change ticket mode.");
    } finally {
      setModeBusy(false);
    }
  };

  const sendCodes = async () => {
    setCodesBusy(true);
    try {
      const { data } = await api.post("/early-access/send-codes", { discount_percent: parseInt(codesDiscount, 10) || 0, audience: codesAudience });
      toast.success(`Codes sent — ${data.sent} emailed (${data.already_sent} already had theirs).`);
      setCodesConfirm(false);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not send codes.");
    } finally {
      setCodesBusy(false);
    }
  };

  const eventInputCls = "w-full bg-transparent border-b border-white/20 py-3 text-[#FDFBF7] placeholder-[#5c5850] focus:outline-none focus:border-[#D4AF37] transition-colors";

  const [audiences, setAudiences] = useState(null);
  const [blastAudience, setBlastAudience] = useState("everyone");
  const [blastSubject, setBlastSubject] = useState("");
  const [blastMessage, setBlastMessage] = useState("");  // full HTML body — Jay composes it, nothing preset
  const [blastHtml, setBlastHtml] = useState(null);
  const [blastCount, setBlastCount] = useState(0);
  const [blastBusy, setBlastBusy] = useState("");
  const [blastConfirm, setBlastConfirm] = useState(false);
  const [campaigns, setCampaigns] = useState([]);
  const [imgSize, setImgSize] = useState("100");
  const [imgAlign, setImgAlign] = useState("center");
  const blastBodyRef = useRef(null);
  const blastImgInputRef = useRef(null);
  const [sectionEvent, setSectionEvent] = useState("ep3-nov28");
  const [tierForm, setTierForm] = useState({ name: "", area: "", guests: "8", deposit: "100", min_bottles: "0", capacity: "1", per_guest: false });
  const [manualForm, setManualForm] = useState({ section_id: "", name: "", email: "", phone: "", party_size: "4", pay_method: "Cash App", deposit: "", balance: "0", note: "", send_receipt: true });
  const [manualBusy, setManualBusy] = useState(false);
  const [tierBusy, setTierBusy] = useState("");
  const [ep3Tier, setEp3Tier] = useState("early");
  const [tierSwitchBusy, setTierSwitchBusy] = useState(false);
  const [scheduled, setScheduled] = useState([]);
  const [schedEvent, setSchedEvent] = useState("");
  const [schedSubject, setSchedSubject] = useState("");
  const [schedMessage, setSchedMessage] = useState("");
  const [schedBusy, setSchedBusy] = useState(false);
  const [smsStatus, setSmsStatus] = useState(null);
  const [smsAudiences, setSmsAudiences] = useState(null);
  const [smsAudience, setSmsAudience] = useState("everyone");
  const [smsMessage, setSmsMessage] = useState("Jay Atk here — Oct 4 is my birthday celebration (Pretty Girls Love R&B, 3–8PM, St. Louis) and Halloween night we're giving away $1,000 for the best costume. Presale tickets move fast: jayatk.com");
  const [smsBusy, setSmsBusy] = useState("");
  const [smsConfirm, setSmsConfirm] = useState(false);
  const [smsTestPhone, setSmsTestPhone] = useState("");
  const [sectionsData, setSectionsData] = useState(null);
  const [sectionsBusy, setSectionsBusy] = useState("");
  const [markPaidConfirm, setMarkPaidConfirm] = useState("");
  const [markPaidMethod, setMarkPaidMethod] = useState("Apple Pay");
  const [markPaidNote, setMarkPaidNote] = useState("");

  const markSectionPaid = async (sessionId) => {
    setSectionsBusy(sessionId);
    try {
      const { data } = await api.post(`/sections/reservations/${sessionId}/mark-paid`, { method: markPaidMethod, note: markPaidNote.trim() });
      toast.success(data.receipt_sent
        ? "Marked fully paid — receipt emailed (and texted if a phone is on file)."
        : "Marked fully paid, but the receipt email failed — send it manually.");
      setMarkPaidConfirm("");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not mark paid.");
    } finally {
      setSectionsBusy("");
    }
  };

  const toggleSections = async () => {
    setSectionsBusy("toggle");
    try {
      const { data } = await api.post("/sections/toggle", { enabled: !sectionsData?.enabled });
      setSectionsData((d) => ({ ...d, enabled: data.enabled }));
      toast.success(data.enabled ? "Sections page is live on the site." : "Sections page hidden from the site.");
    } catch {
      toast.error("Could not update sections.");
    } finally {
      setSectionsBusy("");
    }
  };

  const cancelReservation = async (sessionId) => {
    setSectionsBusy(sessionId);
    try {
      await api.post(`/sections/reservations/${sessionId}/cancel`);
      toast.success("Reservation cancelled — the section is available again. (Deposits are non-refundable; handle any courtesy in Stripe.)");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not cancel.");
    } finally {
      setSectionsBusy("");
    }
  };

  const sendBalanceReminders = async () => {
    setSectionsBusy("remind");
    try {
      const { data } = await api.post("/sections/remind-balances");
      toast.success(`Balance reminders sent to ${data.reminded} reservation${data.reminded === 1 ? "" : "s"} (email + text with a pay link).`);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not send reminders.");
    } finally {
      setSectionsBusy("");
    }
  };

  const toggleSoldOut = async () => {
    setSectionsBusy("soldout");
    try {
      const { data } = await api.post("/sections/soldout", { sold_out: !sectionsData?.sold_out, event: sectionEvent });
      setSectionsData((d) => ({ ...d, sold_out: data.sold_out }));
      toast.success(data.sold_out
        ? "All sections now show SOLD OUT — new reservations are blocked."
        : "Sections reopened — reservations are available again.");
    } catch {
      toast.error("Could not update the sold-out state.");
    } finally {
      setSectionsBusy("");
    }
  };

  const sendManageLinks = async () => {    setSectionsBusy("links");
    try {
      const { data } = await api.post("/sections/send-manage-links");
      toast.success(`Thank-you emails with manage links sent to ${data.sent} section holder${data.sent === 1 ? "" : "s"}${data.skipped ? ` (${data.skipped} skipped)` : ""}.`);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not send manage links.");
    } finally {
      setSectionsBusy("");
    }
  };

  const manualBook = async () => {
    if (!manualForm.section_id) { toast.error("Pick the section first."); return; }
    if (!manualForm.name.trim() || !manualForm.email.trim()) { toast.error("Name and email are required."); return; }
    if (manualForm.deposit === "" || isNaN(Number(manualForm.deposit))) { toast.error("Enter the deposit amount they paid."); return; }
    setManualBusy(true);
    try {
      const { data } = await api.post("/sections/manual-book", {
        section_id: manualForm.section_id, name: manualForm.name.trim(), email: manualForm.email.trim(),
        phone: manualForm.phone.trim(), party_size: Number(manualForm.party_size) || 1,
        pay_method: manualForm.pay_method,
        deposit_cents: Math.round(Number(manualForm.deposit) * 100),
        balance_cents: Math.round(Number(manualForm.balance || 0) * 100),
        note: manualForm.note, send_receipt: manualForm.send_receipt,
      });
      toast.success(`${data.section_name} booked for ${manualForm.name} — confirmation #${data.reservation_no}${data.receipt_sent ? ", receipt emailed" : ""}.`);
      setManualForm({ section_id: "", name: "", email: "", phone: "", party_size: "4", pay_method: "Cash App", deposit: "", balance: "0", note: "", send_receipt: true });
      loadSections(sectionEvent);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not book the section.");
    } finally {
      setManualBusy(false);
    }
  };


  const [hookahData, setHookahData] = useState(null);
  const [hookahBusy, setHookahBusy] = useState("");
  const [hookahTotalInput, setHookahTotalInput] = useState("20");

  const saveHookahConfig = async (enabled) => {
    setHookahBusy("config");
    try {
      const { data } = await api.post("/hookah/config", {
        enabled: enabled !== undefined ? enabled : hookahData?.enabled,
        total: parseInt(hookahTotalInput, 10) || hookahData?.total,
      });
      setHookahData((d) => ({ ...d, enabled: data.enabled, total: data.total, sold: data.sold, remaining: Math.max(0, data.total - data.sold) }));
      toast.success(`Hookah preorders ${data.enabled ? "ON" : "OFF"} · ${data.total} total.`);
    } catch {
      toast.error("Could not save hookah settings.");
    } finally {
      setHookahBusy("");
    }
  };

  const redeemHookah = async (sessionId) => {
    setHookahBusy(sessionId);
    try {
      const { data } = await api.post(`/hookah/${sessionId}/redeem`);
      setHookahData((d) => ({ ...d, orders: d.orders.map((o) => o.session_id === sessionId ? { ...o, redeemed: data.redeemed } : o) }));
      toast.success(data.redeemed ? "Marked picked up." : "Pickup undone.");
    } catch {
      toast.error("Could not update pickup.");
    } finally {
      setHookahBusy("");
    }
  };

  const scannerRef = useRef(null);
  const [scanning, setScanning] = useState(false);
  const [doorTicket, setDoorTicket] = useState(null);
  const [doorError, setDoorError] = useState("");
  const [manualCode, setManualCode] = useState("");
  const [doorBusy, setDoorBusy] = useState(false);
  const [doorFlash, setDoorFlash] = useState("");
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [doorManifest, setDoorManifest] = useState(null);
  const [doorManifestAt, setDoorManifestAt] = useState("");
  const [doorQueue, setDoorQueue] = useState(() => JSON.parse(localStorage.getItem("doorQueue") || "[]"));

  const refreshManifest = async () => {
    try {
      const { data } = await api.get("/checkin/manifest");
      setDoorManifest(data.guests);
      setDoorManifestAt(data.generated_at);
      localStorage.setItem("doorManifest", JSON.stringify({ guests: data.guests, at: data.generated_at }));
    } catch { /* offline — keep the cached list */ }
  };

  const syncQueue = async () => {
    const queue = JSON.parse(localStorage.getItem("doorQueue") || "[]");
    if (!queue.length) return;
    const merged = {};
    queue.forEach((c) => { merged[c] = (merged[c] || 0) + 1; });
    try {
      const { data } = await api.post("/checkin/sync",
        { entries: Object.entries(merged).map(([code, add]) => ({ code, add })) });
      localStorage.setItem("doorQueue", "[]");
      setDoorQueue([]);
      const n = data.synced.reduce((a, s) => a + (s.applied || 0), 0);
      toast.success(`Back online — synced ${n} queued check-in${n === 1 ? "" : "s"}.`);
      refreshManifest();
      load();
    } catch { /* still offline */ }
  };

  useEffect(() => {
    const cached = localStorage.getItem("doorManifest");
    if (cached) {
      try {
        const parsed = JSON.parse(cached);
        setDoorManifest(parsed.guests);
        setDoorManifestAt(parsed.at);
      } catch { /* corrupt cache — ignore */ }
    }
  }, []);

  useEffect(() => {
    const on = () => { setIsOnline(true); syncQueue(); };
    const off = () => setIsOnline(false);
    window.addEventListener("online", on);
    window.addEventListener("offline", off);
    return () => { window.removeEventListener("online", on); window.removeEventListener("offline", off); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (tab === "door" && navigator.onLine) refreshManifest();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  const extractCode = (text) => {
    const m = (text || "").match(/checkin\/([A-Za-z0-9]+)/i);
    return (m ? m[1] : text || "").trim().toUpperCase();
  };

  const lookupCode = async (raw) => {
    const code = extractCode(raw);
    if (!code) return;
    setDoorBusy(true);
    setDoorError("");
    setDoorTicket(null);
    try {
      const { data } = await api.get(`/checkin/${code}`);
      setDoorTicket({ ...data, code });
    } catch (err) {
      if (!err.response && doorManifest) {
        // no connection — search the cached door list
        const g = doorManifest.find((x) => x.code === code);
        if (g) {
          const counts = staffBoot?.tickets || analytics?.tickets || [];
          const titles = g.events.map((slug) => counts.find((t) => t.slug === slug)?.title || slug);
          setDoorTicket({ code, events: titles.length ? titles : ["Jay Atk Events"], quantity: g.quantity,
            checked_in_count: g.checked_in_count, order_ref: g.order_ref, offline: true });
        } else {
          setDoorError("Offline — that code isn't in the cached door list. Reconnect or check the code.");
        }
      } else {
        setDoorError("Not a valid ticket — check the QR and try again.");
      }
      try { scannerRef.current?.resume(); } catch { /* noop */ }
    } finally {
      setDoorBusy(false);
    }
  };

  const swallowScannerRejection = (e) => {
    // html5-qrcode's internal video.play() rejects when the stream element is
    // detached (tab switch / fake devices) — harmless, don't crash the page.
    if (String(e?.reason?.message || e?.reason || "").includes("play()")) {
      e.preventDefault();
    }
  };

  const startScanner = async () => {
    setDoorError("");
    setDoorTicket(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: true });
      stream.getTracks().forEach((t) => t.stop());
    } catch {
      setDoorError("Camera unavailable — use manual code entry below.");
      return;
    }
    window.addEventListener("unhandledrejection", swallowScannerRejection);
    try {
      const { Html5Qrcode } = await import("html5-qrcode");
      const scanner = new Html5Qrcode("door-scanner");
      scannerRef.current = scanner;
      await scanner.start({ facingMode: "environment" }, { fps: 8, qrbox: { width: 240, height: 240 } },
        (decoded) => {
          try { scanner.pause(true); } catch { /* noop */ }
          lookupCode(decoded);
        }, () => {});
      setScanning(true);
    } catch {
      window.removeEventListener("unhandledrejection", swallowScannerRejection);
      try { scannerRef.current?.clear(); } catch { /* noop */ }
      scannerRef.current = null;
      setDoorError("Camera unavailable — use manual code entry below.");
    }
  };

  const stopScanner = async () => {
    window.removeEventListener("unhandledrejection", swallowScannerRejection);
    try { await scannerRef.current?.stop(); scannerRef.current?.clear(); } catch { /* noop */ }
    scannerRef.current = null;
    setScanning(false);
  };

  const nextGuest = () => {
    setDoorTicket(null);
    setDoorFlash("");
    setDoorError("");
    try { scannerRef.current?.resume(); } catch { /* noop */ }
  };

  const doorCheckIn = async () => {
    if (!doorTicket) return;
    setDoorBusy(true);
    setDoorError("");
    try {
      const { data } = await api.post(`/checkin/${doorTicket.code}`);
      setDoorTicket((t) => ({ ...t, ...data }));
      setDoorFlash(`Checked in — ${data.checked_in_count} of ${data.quantity} in`);
      refreshManifest();
      load();
    } catch (err) {
      if (!err.response) {
        // offline — queue it, update the local cache, sync when we're back
        const queue = JSON.parse(localStorage.getItem("doorQueue") || "[]");
        queue.push(doorTicket.code);
        localStorage.setItem("doorQueue", JSON.stringify(queue));
        setDoorQueue(queue);
        const newCount = Math.min(doorTicket.quantity, doorTicket.checked_in_count + 1);
        setDoorTicket((t) => ({ ...t, checked_in_count: newCount }));
        setDoorManifest((m) => {
          if (!m) return m;
          const nm = m.map((g) => g.code === doorTicket.code ? { ...g, checked_in_count: newCount } : g);
          localStorage.setItem("doorManifest", JSON.stringify({ guests: nm, at: doorManifestAt }));
          return nm;
        });
        setDoorFlash(`Queued offline — ${newCount} of ${doorTicket.quantity} in (syncs when back online)`);
      } else {
        setDoorError(formatApiErrorDetail(err.response?.data?.detail) || "Could not check in.");
      }
    } finally {
      setDoorBusy(false);
    }
  };

  useEffect(() => {
    if (tab !== "door") stopScanner();
  }, [tab]);

  const sendSmsBlast = async (test) => {
    setSmsBusy(test ? "test" : "send");
    try {
      const { data } = await api.post("/sms/send", { audience: smsAudience, message: smsMessage, test, test_phone: smsTestPhone });
      toast.success(test ? "Test text sent — check that phone." : `Text blast sent — ${data.sent} of ${data.total} delivered.`);
      setSmsConfirm(false);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not send texts.");
    } finally {
      setSmsBusy("");
    }
  };

  const pickSchedEvent = (slug) => {
    setSchedEvent(slug);
    const ev = eventList.find((e) => e.slug === slug);
    if (!ev) return;
    setSchedSubject(`Tomorrow night: ${ev.title}`);
    setSchedMessage(`It's almost here — ${ev.title} goes down tomorrow, ${ev.date_label}.\n\n${ev.blurb || ""}\n\nLast chance for presale tickets: jayatk.com\n\nGood music. Good people. Good vibes.`);
  };

  const scheduleBlast = async () => {
    if (!schedEvent || !schedSubject.trim() || !schedMessage.trim()) {
      toast.error("Pick an event and write a subject and message.");
      return;
    }
    setSchedBusy(true);
    try {
      await api.post("/blasts/schedule", { event_slug: schedEvent, subject: schedSubject, message: schedMessage, audience: "everyone" });
      toast.success("Reminder scheduled — it goes out automatically the day before.");
      setSchedEvent(""); setSchedSubject(""); setSchedMessage("");
      const { data } = await api.get("/blasts/scheduled");
      setScheduled(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not schedule.");
    } finally {
      setSchedBusy(false);
    }
  };

  const cancelBlast = async (id) => {
    try {
      await api.delete(`/blasts/scheduled/${id}`);
      setScheduled((s) => s.filter((b) => b.id !== id));
      toast.success("Reminder cancelled.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not cancel.");
    }
  };

  const load = async () => {
    try {
      if (isStaff) {
        const { data } = await api.get("/staff/bootstrap");
        setStaffBoot(data);
        return;
      }
      const [s, st, an, ce, ev, au, ss, sc, ea, pr, sa, sm, tx, sec, hk, pc, xst, tk, camps, gt] = await Promise.all([
        api.get("/submissions"), api.get("/submissions/stats"), api.get("/analytics"),
        api.get("/contest"), api.get("/events"), api.get("/blast/audiences"), api.get("/site/status"), api.get("/blasts/scheduled"), api.get("/early-access"), api.get("/prices"),
        api.get("/sms/audiences"), api.get("/sms/status"), api.get("/tax/config"), api.get("/sections/admin", { params: { event: sectionEvent } }),
        api.get("/hookah/admin"), api.get("/promo-codes"), api.get("/stripe/stats"), api.get("/tickets"),
        api.get("/blast/campaigns"), api.get("/golden-tickets"),
      ]);
      setSubs(s.data);
      setStats(st.data);
      setAnalytics(an.data);
      setContest(ce.data);
      setEventList(ev.data);
      setAudiences(au.data);
      setSiteLive(ss.data.live);
      setEarlyCodeUi(ss.data.early_code_ui !== false);
      setTicketsMode(ss.data.tickets_mode || "off");
      setBundleEnabled(pr.data.bundle_enabled);
      setEp3Tier(pr.data.ep3_tier || "early");
      setPriceForms({
        oct4: String((pr.data.prices?.oct4 ?? 1000) / 100),
        halloween: String((pr.data.prices?.halloween ?? 2500) / 100),
        bundle: String((pr.data.prices?.bundle ?? 3000) / 100),
      });
      setCampaigns(camps.data || []);
      setGoldenList(gt.data || []);
      loadGoldenEligible();
      setScheduled(sc.data);
      setEarly(ea.data);
      setSmsAudiences(sa.data);
      setSmsStatus(sm.data);
      setTaxMode(tx.data.mode || "auto");
      setTaxRate(tx.data.flat_percent ? String(tx.data.flat_percent) : "");
      setSectionsData(sec.data);
      setHookahData(hk.data);
      setHookahTotalInput(String(hk.data.total ?? 20));
      setPromoData(pc.data);
      setStripeStats(xst.data);
      setTicketOrders(tk.data);
      const forms = {};
      (an.data.tickets || []).forEach((t) => {
        const effective = (ev.data || []).find((e) => e.slug === t.slug);
        forms[t.slug] = { total: String(t.total), remaining: String(t.remaining), show_remaining: !!t.show_remaining,
          sales_enabled: effective && effective.sales_enabled !== undefined ? effective.sales_enabled : t.sales_enabled !== false };
      });
      setTicketForms(forms);
    } catch {
      toast.error("Failed to load dashboard data.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const doLogout = () => { logout(); navigate("/admin/login"); };

  const toggleSite = async () => {
    setSiteBusy(true);
    try {
      const { data } = await api.post("/site/status", { live: !siteLive });
      setSiteLive(data.live);
      toast.success(data.live ? "Site is LIVE — everyone can see it now." : "Coming-soon screen is up — the site is hidden.");
    } catch {
      toast.error("Could not change site status.");
    } finally {
      setSiteBusy(false);
    }
  };

  const saveEventTickets = async (slug) => {
    const form = ticketForms[slug] || {};
    const total = parseInt(form.total, 10);
    const remaining = parseInt(form.remaining, 10);
    if (isNaN(total) || isNaN(remaining) || total < 1 || remaining < 0) {
      toast.error("Enter valid numbers (total ≥ 1, remaining ≥ 0).");
      return;
    }
    setSavingEvent(slug);
    try {
      const { data } = await api.post("/tickets/config", { event: slug, total, remaining, show_remaining: !!form.show_remaining, sales_enabled: !!form.sales_enabled });
      setTicketForms((f) => ({ ...f, [slug]: { total: String(data.total), remaining: String(data.remaining), show_remaining: !!data.show_remaining, sales_enabled: !!data.sales_enabled } }));
      toast.success(`Saved — ${data.sales_enabled ? "sales ON" : "sales PAUSED"} · ${data.remaining} of ${data.total} tickets left${data.show_remaining ? " (counter public)" : " (counter hidden)"}.`);
      load();
    } catch {
      toast.error("Could not save ticket count.");
    } finally {
      setSavingEvent("");
    }
  };

  const addEvent = async (e) => {
    e.preventDefault();
    if (!newEvent.title.trim() || !newEvent.date_label.trim()) {
      toast.error("Title and date label are required.");
      return;
    }
    setAddBusy(true);
    try {
      const payload = { ...newEvent, event_datetime: newEvent.event_datetime ? new Date(newEvent.event_datetime).toISOString() : "" };
      await api.post("/events", payload);
      toast.success(`"${newEvent.title}" is live on the site.`);
      setNewEvent({ title: "", date_label: "", event_datetime: "", location: "", tagline: "", blurb: "" });
      setAddOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not add event.");
    } finally {
      setAddBusy(false);
    }
  };

  const removeEvent = async (slug) => {
    try {
      await api.delete(`/events/${slug}`);
      toast.success("Event removed.");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not remove event.");
    }
  };

  const exportCsv = async () => {
    try {
      const token = localStorage.getItem("jayatk_token");
      const res = await fetch(`${API}/submissions/export`, { headers: { Authorization: `Bearer ${token}` } });
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = "jayatk_submissions.csv"; a.click();
      window.URL.revokeObjectURL(url);
      toast.success("CSV exported.");
    } catch {
      toast.error("Export failed.");
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/submissions/${id}`);
      setSubs((s) => s.filter((x) => x.id !== id));
      toast.success("Deleted.");
    } catch {
      toast.error("Delete failed.");
    }
  };

  const exportContestCsv = async () => {
    try {
      const token = localStorage.getItem("jayatk_token");
      const res = await fetch(`${API}/contest/export`, { headers: { Authorization: `Bearer ${token}` } });
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = "jayatk_costume_contest.csv"; a.click();
      window.URL.revokeObjectURL(url);
      toast.success("Contest CSV exported.");
    } catch {
      toast.error("Export failed.");
    }
  };

  const removeContest = async (id) => {
    try {
      await api.delete(`/contest/${id}`);
      setContest((c) => c.filter((x) => x.id !== id));
      toast.success("Deleted.");
    } catch {
      toast.error("Delete failed.");
    }
  };

  const exportEarlyCsv = async () => {
    try {
      const token = localStorage.getItem("jayatk_token");
      const res = await fetch(`${API}/early-access/export`, { headers: { Authorization: `Bearer ${token}` } });
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = "jayatk_early_access.csv"; a.click();
      window.URL.revokeObjectURL(url);
      toast.success("Early access CSV exported.");
    } catch {
      toast.error("Export failed.");
    }
  };

  const removeEarly = async (id) => {
    try {
      await api.delete(`/early-access/${id}`);
      setEarly((c) => c.filter((x) => x.id !== id));
      toast.success("Deleted.");
    } catch {
      toast.error("Delete failed.");
    }
  };

  const [shareBusy, setShareBusy] = useState(false);
  const [shareVersion, setShareVersion] = useState("");

  const uploadShareImage = async (file) => {
    if (!file) return;
    setShareBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/share-image", fd);
      setShareVersion(data.share_image_url.split("v=")[1] || String(Date.now()));
      toast.success("Share thumbnail updated — new shares of the link will use it. (Apps like iMessage/WhatsApp cache the old card for a while.)");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Upload failed.");
    } finally {
      setShareBusy(false);
    }
  };

  const uploadBanner = async (slug, file, slot = 1) => {
    if (!file) return;
    setBannerBusy(`${slug}:${slot}`);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/banners/${slug}?slot=${slot}`, fd);
      toast.success(slot === 1 ? "Banner updated — it's live on the site now." : `Flyer ${slot} added — the event banner now rotates.`);
      const ev = await api.get("/events");
      setEventList(ev.data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Upload failed.");
    } finally {
      setBannerBusy("");
    }
  };

  const deleteBanner = async (slug, slot) => {
    setBannerBusy(`${slug}:${slot}`);
    try {
      await api.delete(`/banners/${slug}?slot=${slot}`);
      toast.success(`Flyer ${slot} removed.`);
      const ev = await api.get("/events");
      setEventList(ev.data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not remove it.");
    } finally {
      setBannerBusy("");
    }
  };

  const previewBlast = async () => {
    setBlastBusy("preview");
    try {
      const { data } = await api.post("/blast/preview", { audience: blastAudience, subject: blastSubject, body_html: blastMessage });
      setBlastHtml(data.html);
      setBlastCount(data.recipients);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Preview failed.");
    } finally {
      setBlastBusy("");
    }
  };

  const sendBlast = async (test) => {
    setBlastBusy(test ? "test" : "send");
    try {
      const { data } = await api.post("/blast/send", { audience: blastAudience, subject: blastSubject, body_html: blastMessage, test, api_base: API });
      toast.success(test ? "Test email sent to your inbox." : `Blast sent — ${data.sent} of ${data.total} delivered. Opens will start counting as people read it.`);
      setBlastConfirm(false);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Send failed.");
    } finally {
      setBlastBusy("");
    }
  };

  const insertAtCursor = (snippet) => {
    const el = blastBodyRef.current;
    if (!el) { setBlastMessage((m) => m + snippet); setBlastHtml(null); return; }
    const start = el.selectionStart ?? blastMessage.length;
    const end = el.selectionEnd ?? blastMessage.length;
    setBlastMessage(blastMessage.slice(0, start) + snippet + blastMessage.slice(end));
    setBlastHtml(null);
    requestAnimationFrame(() => { el.focus(); el.selectionStart = el.selectionEnd = start + snippet.length; });
  };

  const insertLink = () => {
    const url = window.prompt("Link URL (https://…)", "https://jayatk.com");
    if (!url) return;
    const text = window.prompt("Link text", "Get tickets") || url;
    insertAtCursor(`<a href="${url}" style="color:#D4AF37;text-decoration:underline">${text}</a>`);
  };

  const uploadBlastImage = async (file) => {
    if (!file) return;
    setBlastBusy("image");
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/blast/images", fd, { headers: { "Content-Type": "multipart/form-data" } });
      insertAtCursor(`<div style="text-align:${imgAlign};margin:16px 0"><img src="${API}${data.path}" alt="" style="width:${imgSize}%;max-width:100%;height:auto;border-radius:10px" /></div>`);
      toast.success("Image added to the email body.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Image upload failed.");
    } finally {
      setBlastBusy("");
      if (blastImgInputRef.current) blastImgInputRef.current.value = "";
    }
  };

  const switchEp3Tier = async (tier) => {
    setTierSwitchBusy(true);
    try {
      const { data } = await api.post("/prices/ep3/tier", { tier });
      setEp3Tier(data.tier);
      toast.success(`Episode 3 tickets now sell at ${data.price_label}.`);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not switch the price.");
    } finally {
      setTierSwitchBusy(false);
    }
  };

  const searchCustomers = async (e) => {
    e?.preventDefault();
    if (custQ.trim().length < 2) { toast.error("Type at least 2 characters."); return; }
    setCustBusy(true);
    try {
      const { data } = await api.get(`/customers/lookup?q=${encodeURIComponent(custQ.trim())}`);
      setCustResults(data.results || []);
      if (!(data.results || []).length) toast.info("Nobody found — check the spelling.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Search failed.");
    } finally {
      setCustBusy(false);
    }
  };

  const openCustomer = async (email) => {
    setCustProfileBusy(true);
    try {
      const { data } = await api.get(`/customers/${encodeURIComponent(email)}`);
      setCustProfile(data);
      setCustEdit({ name: data.early_access?.name || "", phone: data.early_access?.phone || "" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not load that profile.");
    } finally {
      setCustProfileBusy(false);
    }
  };

  const toggleCustomerCode = async (email, active) => {
    try {
      const { data } = await api.patch(`/customers/${encodeURIComponent(email)}/early-code`, { active });
      toast.success(data.active ? `Code ${data.code} reactivated.` : `Code ${data.code} deactivated — it won't work at checkout.`);
      openCustomer(email);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not update the code.");
    }
  };

  const regenCustomerCode = async (email) => {
    try {
      const { data } = await api.post(`/customers/${encodeURIComponent(email)}/regenerate-code`);
      toast.success(`New code ${data.code} ${data.email_sent ? "emailed to them" : "created — email failed to send, give it to them directly"}. The old code is dead.`);
      openCustomer(email);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not regenerate the code.");
    }
  };

  const saveCustomerProfile = async (email) => {
    try {
      await api.patch(`/customers/${encodeURIComponent(email)}`, {
        name: custEdit.name || undefined, phone: custEdit.phone || undefined });
      toast.success("Profile updated across their records.");
      openCustomer(email);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not update the profile.");
    }
  };

  const loadGoldenEligible = async () => {    try {
      const { data } = await api.get("/golden-tickets/eligible");
      setGoldenEligible(data || []);
    } catch { setGoldenEligible([]); }
  };

  const drawGolden = async () => {
    if (goldenMode === "pick" && goldenPicks.length === 0) {
      toast.error("Pick at least one ticket buyer, or switch to random draw.");
      return;
    }
    setGoldenBusy(true);
    try {
      const { data } = await api.post("/golden-tickets/draw", { ...goldenForm,
        picks: goldenMode === "pick" ? goldenPicks : [],
        origin: window.location.origin.includes("localhost") ? "https://jayatk.com" : window.location.origin });
      toast.success(`${data.winners.length} Golden Ticket${data.winners.length === 1 ? "" : "s"} sent — winner emails are on their way.`);
      setGoldenConfirm(false);
      setGoldenPicks([]);
      load();
      loadGoldenEligible();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not draw winners.");
    } finally {
      setGoldenBusy(false);
    }
  };

  const loadSections = async (ev) => {    try {
      const { data } = await api.get("/sections/admin", { params: { event: ev } });
      setSectionsData(data);
    } catch {
      toast.error("Could not load sections.");
    }
  };

  const addTier = async () => {
    const deposit = Math.round(parseFloat(tierForm.deposit) * 100);
    if (!tierForm.name.trim() || isNaN(deposit) || deposit < 0) {
      toast.error("Give the section a name and a deposit price.");
      return;
    }
    setTierBusy("add");
    try {
      await api.post("/sections/tiers", {
        name: tierForm.name.trim(), area: tierForm.area.trim(),
        guests: parseInt(tierForm.guests, 10) || 8, deposit,
        min_bottles: parseInt(tierForm.min_bottles, 10) || 0,
        capacity: parseInt(tierForm.capacity, 10) || 1,
        per_guest: tierForm.per_guest,
      });
      toast.success("Section added to the Chocolate City layout.");
      setTierForm({ name: "", area: "", guests: "8", deposit: "100", min_bottles: "0", capacity: "1", per_guest: false });
      loadSections(sectionEvent);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not add the section.");
    } finally {
      setTierBusy("");
    }
  };

  const patchTier = async (tierId, patch) => {
    setTierBusy(tierId);
    try {
      await api.post(`/sections/tiers/${tierId}`, patch);
      loadSections(sectionEvent);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not update the section.");
    } finally {
      setTierBusy("");
    }
  };

  const deleteTier = async (tierId) => {
    setTierBusy(tierId);
    try {
      await api.delete(`/sections/tiers/${tierId}`);
      toast.success("Section removed from the layout.");
      loadSections(sectionEvent);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not delete the section.");
    } finally {
      setTierBusy("");
    }
  };

  const filtered = subs.filter((s) =>
    [s.name, s.email, s.song_request, s.artist].join(" ").toLowerCase().includes(q.toLowerCase())
  );

  const maxDayViews = Math.max(1, ...(analytics?.views_by_day || []).map((d) => d.views));
  const totalDeviceViews = Object.values(analytics?.devices || {}).reduce((a, b) => a + b, 0);

  return (
    <div className="min-h-screen bg-[#0A0A0A]" data-testid="admin-dashboard">
      <header className="border-b border-white/10 bg-black/60 backdrop-blur-xl sticky top-0 z-40">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-4 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <img src="/logo-jayatk.png" alt="Jay Atk" className="h-7 w-auto" />
            <span className="ml-1 text-xs uppercase tracking-[0.2em] text-[#A19D94] hidden sm:inline" data-testid="dashboard-role-label">{isStaff ? "Door Staff" : "Control Room"}</span>
          </div>
          <div className="flex items-center gap-3">
            {!isStaff && (
            <button onClick={toggleSite} disabled={siteBusy} data-testid="site-status-toggle"
              className={`inline-flex items-center gap-2 text-xs uppercase tracking-[0.18em] border px-4 py-2.5 transition-colors duration-300 disabled:opacity-50 ${
                siteLive ? "border-[#D4AF37] bg-[#D4AF37] text-black hover:bg-transparent hover:text-[#D4AF37]"
                         : "border-red-400/60 bg-red-950/40 text-red-300 hover:bg-red-900/40"}`}>
              <span className={`w-2 h-2 rounded-full ${siteLive ? "bg-green-700" : "bg-red-400"} ${siteBusy ? "animate-pulse" : ""}`} />
              <span data-testid="site-status-label">{siteBusy ? "…" : siteLive ? "Site Live" : "Coming Soon"}</span>
            </button>
            )}
            <button onClick={doLogout} data-testid="logout-button"
              className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] border border-white/15 text-[#A19D94] px-4 py-2.5 hover:text-[#FDFBF7] hover:border-white/40 transition-colors duration-300">
              <LogOut className="w-4 h-4" /> <span className="hidden sm:inline">Logout</span>
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-8 md:flex md:gap-10">
        {/* Side nav (desktop) / tab bar (mobile) */}
        <aside className="hidden md:block w-52 shrink-0">
          <nav className="sticky top-24 space-y-1" data-testid="admin-nav">
            {visibleTabs.map(({ key, label, Icon }) => (
              <button key={key} onClick={() => setTab(key)} data-testid={`admin-tab-${key}`}
                className={`w-full flex items-center gap-3 text-left text-xs uppercase tracking-[0.18em] px-4 py-3.5 border-l-2 transition-colors duration-300 ${
                  tab === key ? "border-[#D4AF37] text-[#F4D77E] bg-[#D4AF37]/5" : "border-transparent text-[#A19D94] hover:text-[#FDFBF7] hover:border-white/30"}`}>
                <Icon className="w-4 h-4" /> {label}
              </button>
            ))}
          </nav>
        </aside>
        <div className="md:hidden flex gap-2 overflow-x-auto pb-4 mb-6 -mx-1 px-1" data-testid="admin-nav-mobile">
          {visibleTabs.map(({ key, label, Icon }) => (
            <button key={key} onClick={() => setTab(key)} data-testid={`admin-tab-${key}-mobile`}
              className={`shrink-0 inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.15em] border px-4 py-2.5 transition-colors ${
                tab === key ? "border-[#D4AF37] text-[#F4D77E] bg-[#D4AF37]/10" : "border-white/15 text-[#A19D94]"}`}>
              <Icon className="w-3.5 h-3.5" /> {label}
            </button>
          ))}
        </div>

        <main className="flex-1 min-w-0">

        {tab === "overview" && (
          <div data-testid="section-overview">
            <SectionHead icon={TrendingUp} title="Overview"
              sub={`Welcome back${user?.name ? `, ${user.name}` : ""} — everything at a glance.`} />
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 mb-4" data-testid="analytics-grid">
              <StatCard icon={Eye} label="All-Time Views" value={analytics?.page_views ?? "—"} />
              <StatCard icon={UserCheck} label="Unique Visitors" value={analytics?.unique_visitors ?? "—"} />
              <StatCard icon={CalendarClock} label="Views Today" value={analytics?.views_today ?? "—"} />
              <StatCard icon={TrendingUp} label="Conversion" value={analytics != null ? `${analytics.conversion_rate}%` : "—"} />
            </div>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 mb-8">
              <StatCard icon={TicketIcon} label="Tickets Sold" value={analytics?.tickets_sold ?? "—"} />
              <StatCard icon={Flame} label="Tickets Left" value={analytics?.tickets ? `${analytics.tickets.reduce((a, t) => a + t.remaining, 0)}/${analytics.tickets.reduce((a, t) => a + t.total, 0)}` : "—"} />
              <StatCard icon={DollarSign} label="Revenue" value={analytics != null ? `$${analytics.revenue}` : "—"} />
              <StatCard icon={Music} label="Top Song" value={analytics?.top_songs?.[0]?.song || "—"} />
            </div>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 mb-8" data-testid="stripe-stats-grid">
              <StatCard icon={DollarSign} label="Stripe Revenue · All-Time" value={stripeStats ? `$${(stripeStats.gross_cents / 100).toLocaleString(undefined, { minimumFractionDigits: 2 })}` : "—"} />
              <StatCard icon={TicketIcon} label="Stripe Tickets Sold" value={stripeStats?.tickets_sold ?? "—"} />
              <StatCard icon={Zap} label="Stripe Checkouts" value={stripeStats?.sessions ?? "—"} />
              <StatCard icon={Armchair} label="Oct 4 · Halloween · Bundle" value={stripeStats ? `${stripeStats.by_product?.oct4 || 0} · ${stripeStats.by_product?.halloween || 0} · ${stripeStats.by_product?.bundle || 0}` : "—"} />
            </div>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 mb-8">
              <StatCard icon={Zap} label="Early Access" value={analytics?.early_access ?? "—"} />
              <StatCard icon={Trophy} label="Contest Entries" value={analytics?.contest_entries ?? "—"} />
              <StatCard icon={Users} label="Song Requests" value={stats?.total ?? "—"} />
              <StatCard icon={CalendarClock} label="Requests Today" value={stats?.today ?? "—"} />
            </div>

            {/* 14-day views chart */}
            <div className="border border-white/10 bg-black/40 p-6 mb-4" data-testid="views-chart">
              <span className="text-xs uppercase tracking-[0.25em] text-[#D4AF37]">Views · Last 14 Days</span>
              <div className="mt-6 flex items-end gap-1.5 md:gap-2.5 h-36">
                {(analytics?.views_by_day || []).map((d) => (
                  <div key={d.date} className="flex-1 h-full flex flex-col items-center justify-end gap-2 group" title={`${d.date}: ${d.views} views`}>
                    <span className="text-[10px] text-[#A19D94] opacity-0 group-hover:opacity-100 transition-opacity tabular-nums">{d.views}</span>
                    <div className="w-full bg-gradient-to-t from-[#8C7324]/40 to-[#D4AF37] transition-all duration-500 group-hover:from-[#8C7324] group-hover:to-[#F4D77E]"
                      style={{ height: `${Math.max(3, (d.views / maxDayViews) * 100)}%` }} data-testid={`views-bar-${d.date.replace(" ", "-")}`} />
                    <span className="text-[9px] uppercase tracking-wide text-[#5c5850] whitespace-nowrap hidden sm:block">{d.date}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="grid md:grid-cols-3 gap-4 mb-4">
              {/* Locations */}
              <div className="border border-white/10 bg-black/40 p-6" data-testid="top-locations">
                <div className="flex items-center gap-2 mb-5">
                  <MapPin className="w-4 h-4 text-[#D4AF37]" />
                  <span className="text-xs uppercase tracking-[0.22em] text-[#D4AF37]">Top Locations</span>
                </div>
                {(analytics?.top_locations || []).length === 0 ? (
                  <p className="text-sm text-[#5c5850]">Location data builds as visitors arrive.</p>
                ) : (
                  <div className="space-y-3">
                    {analytics.top_locations.map((l) => (
                      <div key={l.location} className="flex items-center justify-between gap-3" data-testid={`location-${l.location.replace(/\s+/g, "-")}`}>
                        <span className="text-sm text-[#FDFBF7] truncate">{l.location}</span>
                        <span className="text-xs text-[#A19D94] tabular-nums shrink-0">{l.count} views</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              {/* Pages */}
              <div className="border border-white/10 bg-black/40 p-6" data-testid="top-pages">
                <div className="flex items-center gap-2 mb-5">
                  <Globe className="w-4 h-4 text-[#D4AF37]" />
                  <span className="text-xs uppercase tracking-[0.22em] text-[#D4AF37]">Top Pages</span>
                </div>
                <div className="space-y-3">
                  {(analytics?.top_pages || []).map((p) => (
                    <div key={p.path} className="flex items-center justify-between gap-3">
                      <span className="text-sm text-[#FDFBF7] truncate">{p.path === "/" ? "Home" : p.path}</span>
                      <span className="text-xs text-[#A19D94] tabular-nums shrink-0">{p.count} views</span>
                    </div>
                  ))}
                </div>
              </div>
              {/* Devices + referrers */}
              <div className="border border-white/10 bg-black/40 p-6" data-testid="devices-card">
                <div className="flex items-center gap-2 mb-5">
                  <Monitor className="w-4 h-4 text-[#D4AF37]" />
                  <span className="text-xs uppercase tracking-[0.22em] text-[#D4AF37]">Devices</span>
                </div>
                {totalDeviceViews === 0 ? (
                  <p className="text-sm text-[#5c5850]">Device data builds as visitors arrive.</p>
                ) : (
                  <div className="space-y-4">
                    {[["mobile", Smartphone], ["desktop", Monitor]].map(([key, Icon]) => {
                      const n = analytics.devices[key] || 0;
                      const pct = Math.round((n / totalDeviceViews) * 100);
                      return (
                        <div key={key} data-testid={`device-${key}`}>
                          <div className="flex items-center justify-between text-sm mb-1.5">
                            <span className="inline-flex items-center gap-2 text-[#FDFBF7] capitalize"><Icon className="w-3.5 h-3.5 text-[#8C7324]" />{key}</span>
                            <span className="text-xs text-[#A19D94] tabular-nums">{pct}%</span>
                          </div>
                          <div className="h-1.5 bg-white/10"><div className="h-full bg-[#D4AF37]" style={{ width: `${pct}%` }} /></div>
                        </div>
                      );
                    })}
                  </div>
                )}
                {(analytics?.top_referrers || []).length > 0 && (
                  <div className="mt-6 pt-5 border-t border-white/10">
                    <span className="text-[10px] uppercase tracking-[0.22em] text-[#A19D94]">Traffic Sources</span>
                    <div className="mt-3 space-y-2">
                      {analytics.top_referrers.slice(0, 3).map((r) => (
                        <div key={r.source} className="flex items-center justify-between gap-3">
                          <span className="text-xs text-[#A19D94] truncate">{r.source.replace(/^https?:\/\//, "").slice(0, 32)}</span>
                          <span className="text-xs text-[#5c5850] tabular-nums shrink-0">{r.count}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {tab === "events" && (
          <div data-testid="section-events">
            <SectionHead icon={TicketIcon} title="Events" sub="Ticket availability, new events, and banner artwork." />

            <div className="border border-[#D4AF37]/25 bg-black/40 p-6 mb-6" data-testid="ticket-config">
              <div className="flex flex-wrap items-center justify-between gap-4 mb-2">
                <span className="text-xs uppercase tracking-[0.25em] text-[#D4AF37]">Ticket Availability</span>
                <div className="flex items-center gap-2" data-testid="tickets-mode-switcher">
                  <span className="text-[10px] uppercase tracking-[0.2em] text-[#A19D94] mr-1">Ticket sales:</span>
                  {[["off", "Off"], ["early", "Early Window"], ["public", "Public"]].map(([m, label]) => (
                    <button key={m} onClick={() => setMode(m)} disabled={modeBusy} data-testid={`tickets-mode-${m}`}
                      className={`text-[10px] uppercase tracking-[0.15em] border px-4 py-2 transition-colors duration-300 disabled:opacity-50 ${
                        ticketsMode === m ? "border-[#D4AF37] bg-[#D4AF37] text-black" : "border-white/15 text-[#A19D94] hover:border-[#D4AF37]/50"}`}>
                      {label}
                    </button>
                  ))}
                </div>
              </div>
              <p className="text-sm text-[#A19D94] mb-6">
                {ticketsMode === "off" && "Sales are off — the Oct 10 tab collects early access signups and no prices or buy buttons show anywhere."}
                {ticketsMode === "early" && "Early window — only fans with a valid code can check out (24 hours before public sale)."}
                {ticketsMode === "public" && "Public sale — anyone can buy."}
                {" "}Counts drop automatically with every live ticket sold — set remaining to 0 to force “Sold Out.”
              </p>
              <div className="space-y-6">
                {(analytics?.tickets || []).map((t) => {
                  const form = ticketForms[t.slug] || { total: "", remaining: "" };
                  return (
                    <div key={t.slug} className="border border-white/10 bg-black/30 p-5" data-testid={`ticket-config-${t.slug}`}>
                      <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
                        <span className="font-serif-display text-xl text-[#FDFBF7]">{t.title}</span>
                        <span className="text-[11px] uppercase tracking-[0.2em] text-[#A19D94]" data-testid={`ticket-sold-${t.slug}`}>
                          {t.sold} sold · {t.remaining} left
                        </span>
                        <span className="text-[11px] uppercase tracking-[0.2em] text-[#8C7324]" data-testid={`ticket-checked-in-${t.slug}`}>
                          {t.checked_in ?? 0} checked in
                        </span>
                      </div>
                      <div className="flex flex-wrap items-end gap-4">
                        <label className="block">
                          <span className="text-[11px] uppercase tracking-[0.2em] text-[#A19D94]">Batch total</span>
                          <input data-testid={`ticket-total-input-${t.slug}`} type="number" min="1" value={form.total}
                            onChange={(e) => setTicketForms((f) => ({ ...f, [t.slug]: { ...form, total: e.target.value } }))}
                            className="mt-2 w-28 bg-transparent border-b border-white/20 py-2 text-[#FDFBF7] focus:outline-none focus:border-[#D4AF37]" />
                        </label>
                        <label className="block">
                          <span className="text-[11px] uppercase tracking-[0.2em] text-[#A19D94]">Tickets left</span>
                          <input data-testid={`ticket-remaining-input-${t.slug}`} type="number" min="0" value={form.remaining}
                            onChange={(e) => setTicketForms((f) => ({ ...f, [t.slug]: { ...form, remaining: e.target.value } }))}
                            className="mt-2 w-28 bg-transparent border-b border-white/20 py-2 text-[#FDFBF7] focus:outline-none focus:border-[#D4AF37]" />
                        </label>
                        <label className="flex items-center gap-2 cursor-pointer select-none pb-1">
                          <input type="checkbox" data-testid={`ticket-show-toggle-${t.slug}`} checked={!!form.show_remaining}
                            onChange={(e) => setTicketForms((f) => ({ ...f, [t.slug]: { ...form, show_remaining: e.target.checked } }))}
                            className="w-4 h-4 accent-[#D4AF37]" />
                          <span className="text-[11px] uppercase tracking-[0.2em] text-[#A19D94]">Show counter publicly</span>
                        </label>
                        <label className="flex items-center gap-2 cursor-pointer select-none pb-1">
                          <input type="checkbox" data-testid={`ticket-sales-toggle-${t.slug}`} checked={!!form.sales_enabled}
                            onChange={(e) => setTicketForms((f) => ({ ...f, [t.slug]: { ...form, sales_enabled: e.target.checked } }))}
                            className="w-4 h-4 accent-[#D4AF37]" />
                          <span className={`text-[11px] uppercase tracking-[0.2em] ${form.sales_enabled ? "text-[#F4D77E]" : "text-red-300"}`}>Ticket sales live</span>
                        </label>
                        {t.slug === "ep3-nov28" && (
                          <span className="text-[10px] uppercase tracking-[0.15em] text-[#8C7324] pb-1" data-testid="ep3-auto-open-note">
                            Auto-opens Friday 12:00 AM CT — saving here overrides that
                          </span>
                        )}
                        <button onClick={() => saveEventTickets(t.slug)} disabled={savingEvent === t.slug} data-testid={`save-tickets-button-${t.slug}`}
                          className="text-xs uppercase tracking-[0.2em] border border-[#D4AF37] bg-[#D4AF37] text-black px-6 py-3 hover:bg-transparent hover:text-[#D4AF37] transition-colors duration-300 disabled:opacity-50">
                          {savingEvent === t.slug ? "Saving…" : "Save"}
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="border border-white/10 bg-black/40 p-6 mb-6" data-testid="pricing-card">
              <div className="flex flex-wrap items-center justify-between gap-4 mb-2">
                <span className="text-xs uppercase tracking-[0.25em] text-[#D4AF37]">Pricing</span>
              </div>
              <p className="text-sm text-[#A19D94] mb-6">Episode 3 sells at early-bird price until you flip the switch — new checkouts charge the active price instantly.</p>
              <div className="grid sm:grid-cols-2 gap-5">
                <div className="border border-[#D4AF37]/30 bg-black/30 p-5" data-testid="ep3-tier-card">
                  <span className="text-[11px] uppercase tracking-[0.18em] text-[#A19D94]">Episode 3: Chocolate City · Nov 28</span>
                  <div className="flex flex-wrap gap-3 mt-4">
                    {[["early", "$20 Early Bird"], ["general", "$25 General Admission"]].map(([key, label]) => (
                      <button key={key} onClick={() => switchEp3Tier(key)} disabled={tierSwitchBusy || ep3Tier === key} data-testid={`ep3-tier-${key}`}
                        className={`text-xs uppercase tracking-[0.15em] border px-5 py-3 transition-colors duration-300 disabled:opacity-60 ${
                          ep3Tier === key ? "border-[#D4AF37] bg-[#D4AF37] text-black" : "border-white/15 text-[#A19D94] hover:border-[#D4AF37]/40"}`}>
                        {label}
                      </button>
                    ))}
                  </div>
                  <p className="text-[10px] uppercase tracking-[0.2em] text-[#8C7324] mt-3" data-testid="ep3-tier-active">
                    Now charging: {ep3Tier === "early" ? "$20 Early Bird" : "$25 General Admission"}
                  </p>
                </div>
                {[["oct4", "Pretty Girls Love R&B (past event)"]].map(([key, label]) => (
                  <div key={key} className="border border-white/10 bg-black/30 p-4" data-testid={`price-row-${key}`}>
                    <span className="text-[11px] uppercase tracking-[0.18em] text-[#A19D94]">{label}</span>
                    <div className="flex items-end gap-3 mt-3">
                      <label className="block flex-1">
                        <span className="text-[10px] text-[#5c5850]">Price ($)</span>
                        <input type="number" min="1" step="1" value={priceForms[key]} data-testid={`price-input-${key}`}
                          onChange={(e) => setPriceForms((f) => ({ ...f, [key]: e.target.value }))}
                          className="mt-1 w-full bg-transparent border-b border-white/20 py-2 text-[#FDFBF7] text-lg focus:outline-none focus:border-[#D4AF37]" />
                      </label>
                      <button onClick={() => savePrice(key)} disabled={savingPrice === key} data-testid={`save-price-${key}`}
                        className="text-[10px] uppercase tracking-[0.15em] border border-[#D4AF37] bg-[#D4AF37] text-black px-4 py-2.5 hover:bg-transparent hover:text-[#D4AF37] transition-colors duration-300 disabled:opacity-50">
                        {savingPrice === key ? "…" : "Save"}
                      </button>
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-6 border-t border-white/10 pt-6" data-testid="tax-section">
                <span className="text-[11px] uppercase tracking-[0.25em] text-[#D4AF37]">Sales Tax</span>
                <p className="text-sm text-[#A19D94] mt-2 mb-4">Address-based lets Stripe calculate each buyer's exact rate. Flat venue rate charges every buyer the confirmed St. Louis combined rate (baseline 9.679% — downtown special districts may add ~0.5–2%; confirm the venue's exact rate with your accountant).</p>
                <div className="flex flex-wrap items-end gap-4">
                  <div className="flex gap-3">
                    <button onClick={() => setTaxMode("auto")} data-testid="tax-mode-auto"
                      className={`text-xs uppercase tracking-[0.15em] border px-5 py-3 transition-colors duration-300 ${
                        taxMode === "auto" ? "border-[#D4AF37] bg-[#D4AF37]/10 text-[#F4D77E]" : "border-white/15 text-[#A19D94] hover:border-[#D4AF37]/40"}`}>
                      Address-based
                    </button>
                    <button onClick={() => setTaxMode("flat")} data-testid="tax-mode-flat"
                      className={`text-xs uppercase tracking-[0.15em] border px-5 py-3 transition-colors duration-300 ${
                        taxMode === "flat" ? "border-[#D4AF37] bg-[#D4AF37]/10 text-[#F4D77E]" : "border-white/15 text-[#A19D94] hover:border-[#D4AF37]/40"}`}>
                      Flat venue rate
                    </button>
                  </div>
                  {taxMode === "flat" && (
                    <label className="block">
                      <span className="text-[10px] text-[#5c5850]">Confirmed rate (%)</span>
                      <input type="number" min="0.1" step="0.001" value={taxRate} data-testid="tax-rate-input"
                        onChange={(e) => setTaxRate(e.target.value)} placeholder="9.679"
                        className="mt-1 w-32 bg-transparent border-b border-white/20 py-2 text-[#FDFBF7] text-lg focus:outline-none focus:border-[#D4AF37]" />
                    </label>
                  )}
                  <button onClick={saveTax} disabled={!!savingTax} data-testid="tax-save-button"
                    className="text-xs uppercase tracking-[0.2em] border border-[#D4AF37] bg-[#D4AF37] text-black px-6 py-3 hover:bg-transparent hover:text-[#D4AF37] transition-colors duration-300 disabled:opacity-50">
                    {savingTax ? "Saving…" : "Save Tax"}
                  </button>
                </div>
              </div>
            </div>

            <div className="border border-white/10 bg-black/40 p-6 mb-6" data-testid="add-event-section">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs uppercase tracking-[0.25em] text-[#D4AF37]">Add Event</span>
                <button onClick={() => setAddOpen(!addOpen)} data-testid="add-event-toggle"
                  className="text-xs uppercase tracking-[0.2em] border border-[#D4AF37]/60 text-[#D4AF37] px-5 py-2.5 hover:bg-[#D4AF37] hover:text-black transition-colors duration-300">
                  {addOpen ? "Close" : "+ New Event"}
                </button>
              </div>
              {addOpen && (
                <form onSubmit={addEvent} className="mt-4 grid md:grid-cols-2 gap-x-8 gap-y-5" data-testid="add-event-form">
                  <input data-testid="add-event-title" placeholder="Event title * (e.g. Friendsgiving R&B Night)" value={newEvent.title}
                    onChange={(e) => setNewEvent((f) => ({ ...f, title: e.target.value }))} className={eventInputCls} />
                  <input data-testid="add-event-date-label" placeholder="Date label * (e.g. November 27)" value={newEvent.date_label}
                    onChange={(e) => setNewEvent((f) => ({ ...f, date_label: e.target.value }))} className={eventInputCls} />
                  <label className="block">
                    <span className="text-[11px] uppercase tracking-[0.2em] text-[#A19D94]">Date & time (optional — powers the countdown)</span>
                    <input data-testid="add-event-datetime" type="datetime-local" value={newEvent.event_datetime}
                      onChange={(e) => setNewEvent((f) => ({ ...f, event_datetime: e.target.value }))} className={`${eventInputCls} [color-scheme:dark]`} />
                  </label>
                  <input data-testid="add-event-location" placeholder="Location (optional)" value={newEvent.location}
                    onChange={(e) => setNewEvent((f) => ({ ...f, location: e.target.value }))} className={eventInputCls} />
                  <input data-testid="add-event-tagline" placeholder="Tagline (optional)" value={newEvent.tagline}
                    onChange={(e) => setNewEvent((f) => ({ ...f, tagline: e.target.value }))} className={eventInputCls} />
                  <textarea data-testid="add-event-description" placeholder="Description — what should guests know?" value={newEvent.blurb} rows={3}
                    onChange={(e) => setNewEvent((f) => ({ ...f, blurb: e.target.value }))}
                    className={`${eventInputCls} md:col-span-2 border border-white/20 p-4 resize-none`} />
                  <div className="md:col-span-2 flex items-center gap-4">
                    <button type="submit" disabled={addBusy} data-testid="add-event-submit"
                      className="text-xs uppercase tracking-[0.2em] border border-[#D4AF37] bg-[#D4AF37] text-black px-8 py-3 hover:bg-transparent hover:text-[#D4AF37] transition-colors duration-300 disabled:opacity-50">
                      {addBusy ? "Publishing…" : "Publish Event"}
                    </button>
                    <p className="text-xs text-[#5c5850]">New events appear on the site instantly. Add a banner below, then ask me to turn on ticketing when ready.</p>
                  </div>
                </form>
              )}
            </div>

            <div className="border border-white/10 bg-black/40 p-6" data-testid="banner-manager">
              <span className="text-xs uppercase tracking-[0.25em] text-[#D4AF37] block mb-2">Event Banners & Flyer Carousel</span>
              <p className="text-sm text-[#A19D94] mb-6">Each event holds up to 3 flyer designs — extras rotate as a carousel on the event card. Changes go live instantly.</p>
              <div className="grid md:grid-cols-2 gap-5">
                {eventList.map((ev) => {
                  const fallback = `/banners/${{ "ep1": "ep1", "pglr-oct4": "oct4", "halloween-oct31": "halloween", "ep3-nov28": "ep3" }[ev.slug]}.jpg`;
                  const urls = (ev.banner_urls && ev.banner_urls.length ? ev.banner_urls : [ev.banner_url || fallback].filter(Boolean));
                  return (
                  <div key={ev.slug} className="border border-white/10 bg-black/30 p-4" data-testid={`banner-card-${ev.slug}`}>
                    <div className="flex gap-2 mb-3">
                      {[0, 1, 2].map((i) => (
                        urls[i]
                          ? <img key={i} src={urls[i]} alt={`${ev.title} flyer ${i + 1}`} className="w-1/3 h-24 object-cover border border-white/10" data-testid={`banner-preview-${ev.slug}-${i + 1}`} />
                          : <div key={i} className="w-1/3 h-24 border border-dashed border-white/10 flex items-center justify-center text-[9px] uppercase tracking-[0.15em] text-[#5c5850]" data-testid={`banner-empty-${ev.slug}-${i + 1}`}>Flyer {i + 1}</div>
                      ))}
                    </div>
                    <div className="min-w-0 mb-3">
                      <div className="text-[#FDFBF7] text-sm truncate">{ev.title}</div>
                      <div className="text-[10px] uppercase tracking-[0.2em] text-[#A19D94] mt-0.5">{ev.date_label}</div>
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                      {[1, 2, 3].map((slot) => (
                        <span key={slot} className="inline-flex items-center gap-1.5">
                          <label className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.15em] border border-white/20 text-[#A19D94] px-3 py-2 hover:border-[#D4AF37] hover:text-[#D4AF37] cursor-pointer transition-colors">
                            <Upload className="w-3 h-3" /> {bannerBusy === `${ev.slug}:${slot}` ? "…" : urls[slot - 1] ? `Swap ${slot}` : `Add ${slot}`}
                            <input type="file" accept="image/jpeg,image/png,image/webp" className="hidden" data-testid={`banner-upload-${ev.slug}-${slot}`}
                              onChange={(e) => { uploadBanner(ev.slug, e.target.files?.[0], slot); e.target.value = ""; }} />
                          </label>
                          {slot > 1 && urls[slot - 1] && (
                            <button onClick={() => deleteBanner(ev.slug, slot)} disabled={!!bannerBusy} data-testid={`banner-delete-${ev.slug}-${slot}`}
                              className="text-[#5c5850] hover:text-red-400 transition-colors" aria-label={`Remove flyer ${slot}`}>
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          )}
                        </span>
                      ))}
                      {ev.custom && (
                        <button onClick={() => removeEvent(ev.slug)} data-testid={`delete-event-${ev.slug}`}
                          className="ml-auto text-[#5c5850] hover:text-red-400 transition-colors" aria-label="Delete event">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </div>
                  );
                })}
              </div>
            </div>

            <div className="border border-white/10 bg-black/40 p-6 mt-6" data-testid="share-image-card">
              <span className="text-xs uppercase tracking-[0.25em] text-[#D4AF37] block mb-2">Link Share Thumbnail</span>
              <p className="text-sm text-[#A19D94] mb-5">The image people see when jayatk.com is shared in iMessage, WhatsApp, Instagram or X. Upload a wide image (about 1200×630 works best). Goes live instantly — apps that already showed the old card may cache it for a while.</p>
              <div className="flex flex-wrap items-center gap-5">
                <img src={`/api/uploads/share-image.jpg${shareVersion ? `?v=${shareVersion}` : ""}`} alt="Current share thumbnail"
                  className="w-64 aspect-[1200/630] object-cover border border-[#D4AF37]/30" data-testid="share-image-preview" />
                <label className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] border border-[#D4AF37] bg-[#D4AF37] text-black px-6 py-3 hover:bg-transparent hover:text-[#D4AF37] cursor-pointer transition-colors duration-300">
                  <Upload className="w-4 h-4" /> {shareBusy ? "Uploading…" : "Change Thumbnail"}
                  <input type="file" accept="image/jpeg,image/png,image/webp" className="hidden" data-testid="share-image-upload"
                    onChange={(e) => { uploadShareImage(e.target.files?.[0]); e.target.value = ""; }} />
                </label>
              </div>
            </div>
          </div>
        )}

        {tab === "requests" && (
          <div data-testid="section-requests">
            <div className="flex items-start justify-between gap-4 mb-8">
              <SectionHead icon={Music} title="Song Requests" sub="Every request and fan captured across your events." />
              <button onClick={exportCsv} data-testid="export-csv-button"
                className="shrink-0 inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] border border-[#D4AF37]/60 text-[#D4AF37] px-5 py-3 hover:bg-[#D4AF37] hover:text-black transition-colors duration-300">
                <Download className="w-4 h-4" /> <span className="hidden sm:inline">Export CSV</span>
              </button>
            </div>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 mb-8">
              <StatCard icon={Users} label="Total Requests" value={stats?.total ?? "—"} />
              <StatCard icon={CalendarClock} label="Today" value={stats?.today ?? "—"} />
              <StatCard icon={Share2} label="With Socials" value={stats?.with_socials ?? "—"} />
              <StatCard icon={Music} label="Top Requested" value={stats?.top_songs?.[0]?.count ?? "—"} />
            </div>

            <div className="flex items-center gap-3 border-b border-white/15 mb-6 max-w-md">
              <Search className="w-4 h-4 text-[#8C7324]" />
              <input data-testid="search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, email or song…"
                className="w-full bg-transparent py-3 text-[#FDFBF7] placeholder-[#5c5850] focus:outline-none" />
            </div>

            {loading ? (
              <p className="text-[#A19D94] py-20 text-center">Loading submissions…</p>
            ) : filtered.length === 0 ? (
              <div className="border border-white/10 bg-black/40 py-24 text-center" data-testid="empty-state">
                <Music className="w-8 h-8 text-[#8C7324] mx-auto mb-4" />
                <p className="text-[#A19D94]">No submissions yet. Share your link and watch the requests roll in.</p>
              </div>
            ) : (
              <div className="space-y-4" data-testid="submissions-list">
                {filtered.map((s) => (
                  <div key={s.id} data-testid={`submission-row-${s.id}`}
                    className="group border border-white/10 bg-black/40 p-6 hover:border-[#D4AF37]/40 transition-colors duration-300">
                    <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                      <div>
                        <div className="flex items-center gap-3 flex-wrap">
                          <Music className="w-4 h-4 text-[#D4AF37]" />
                          <span className="font-serif-display text-2xl text-[#FDFBF7]">{s.song_request}</span>
                          {s.artist && <span className="text-[#A19D94] italic">— {s.artist}</span>}
                        </div>
                        {s.dedication && <p className="text-sm text-[#A19D94] italic mt-2">"{s.dedication}"</p>}
                        <div className="flex flex-wrap items-center gap-x-5 gap-y-2 mt-4 text-sm">
                          <span className="text-[#FDFBF7]">{s.name}</span>
                          <span className="inline-flex items-center gap-1 text-[#A19D94]"><Mail className="w-3.5 h-3.5 text-[#8C7324]" />{s.email}</span>
                          <span className="inline-flex items-center gap-1 text-[#A19D94]"><Phone className="w-3.5 h-3.5 text-[#8C7324]" />{s.phone}</span>
                        </div>
                        <div className="mt-3 flex flex-wrap items-center">
                          <Social icon={Instagram} value={s.instagram} />
                          <Social icon={Facebook} value={s.facebook} />
                          <Social symbol="♪" value={s.tiktok} />
                          <Social icon={Twitter} value={s.twitter} />
                        </div>
                      </div>
                      <div className="flex items-center gap-4 shrink-0">
                        <span className="text-xs uppercase tracking-[0.15em] text-[#5c5850]">
                          {new Date(s.created_at).toLocaleDateString(undefined, { month: "short", day: "numeric" })}
                        </span>
                        <button onClick={() => remove(s.id)} data-testid={`delete-${s.id}`}
                          className="text-[#5c5850] hover:text-red-400 transition-colors" aria-label="Delete">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {tab === "early" && (
          <div data-testid="section-early">
            <div className="flex items-start justify-between gap-4 mb-8">
              <SectionHead icon={Zap} title="Early Access List"
                sub={<span>Teaser page signups — they shop 24 hours early with a personal access code. <span data-testid="early-count">{early.length} signed up</span></span>} />
              <button onClick={exportEarlyCsv} data-testid="export-early-csv-button"
                className="shrink-0 inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] border border-[#D4AF37]/60 text-[#D4AF37] px-5 py-3 hover:bg-[#D4AF37] hover:text-black transition-colors duration-300">
                <Download className="w-4 h-4" /> <span className="hidden sm:inline">Export CSV</span>
              </button>
            </div>

            <div className="border border-[#D4AF37]/25 bg-black/40 p-6 mb-6" data-testid="codes-control">
              <div className="flex flex-wrap items-start justify-between gap-4 mb-2">
                <span className="text-xs uppercase tracking-[0.25em] text-[#D4AF37]">Early Access Codes</span>
                <button onClick={async () => {
                    try {
                      const { data } = await api.post("/early-access/code-ui", { visible: !earlyCodeUi });
                      setEarlyCodeUi(data.visible);
                      toast.success(data.visible ? "Code box is showing on the event card." : "Code box hidden — the event card no longer asks for a code.");
                    } catch { toast.error("Could not update the code box setting."); }
                  }}
                  data-testid="early-code-ui-toggle"
                  className={`text-[10px] uppercase tracking-[0.2em] px-4 py-2 border transition-colors ${earlyCodeUi ? "border-[#D4AF37]/50 text-[#D4AF37] hover:border-[#D4AF37]" : "border-white/15 text-[#5c5850] hover:text-[#A19D94]"}`}>
                  Code box on event card: {earlyCodeUi ? "ON" : "OFF"}
                </button>
              </div>
              <p className="text-sm text-[#A19D94] mb-5">Generates a unique single-use code per person and emails it to them — codes work at Chocolate City checkout. Add a discount percent to make it an early-access discount code, or leave 0 for access at the early-bird price. People who already got a code at this discount are skipped. When the early offer expires, switch the code box off above so visitors stop seeing it.</p>
              <div className="flex flex-wrap items-end gap-4 mb-5">
                <label className="block">
                  <span className="text-[11px] uppercase tracking-[0.2em] text-[#A19D94]">Send to</span>
                  <select value={codesAudience} onChange={(e) => setCodesAudience(e.target.value)} data-testid="codes-audience"
                    className="mt-2 bg-black border border-white/20 px-4 py-3 text-[#FDFBF7] focus:outline-none focus:border-[#D4AF37]">
                    <option value="early">Early access list ({early.length})</option>
                    <option value="everyone">Everyone in the data system</option>
                  </select>
                </label>
                <label className="block">
                  <span className="text-[11px] uppercase tracking-[0.2em] text-[#A19D94]">Discount % (0 = none)</span>
                  <input type="number" min="0" max="90" value={codesDiscount} onChange={(e) => setCodesDiscount(e.target.value)} data-testid="codes-discount"
                    className="mt-2 w-28 bg-transparent border-b border-white/20 py-2 text-[#FDFBF7] text-lg focus:outline-none focus:border-[#D4AF37]" />
                </label>
              </div>
              <div className="flex flex-wrap items-center gap-4">
                {!codesConfirm ? (
                  <button onClick={() => setCodesConfirm(true)} disabled={codesBusy || early.length === 0} data-testid="send-codes-button"
                    className="text-xs uppercase tracking-[0.2em] border border-[#D4AF37] bg-[#D4AF37] text-black px-6 py-3 hover:bg-transparent hover:text-[#D4AF37] transition-colors duration-300 disabled:opacity-50">
                    Generate & Send Codes
                  </button>
                ) : (
                  <span className="inline-flex items-center gap-3 border border-red-400/50 bg-red-950/30 px-5 py-3" data-testid="codes-confirm">
                    <span className="text-xs uppercase tracking-[0.15em] text-red-300">
                      Email codes to {codesAudience === "early" ? "the early list" : "EVERYONE in the data system"}{parseInt(codesDiscount, 10) >= 5 ? ` with ${codesDiscount}% off` : ""}?
                    </span>
                    <button onClick={sendCodes} disabled={codesBusy} data-testid="codes-confirm-send"
                      className="text-xs uppercase tracking-[0.15em] text-[#F4D77E] hover:text-white transition-colors">
                      {codesBusy ? "Sending…" : "Yes, Send"}
                    </button>
                    <button onClick={() => setCodesConfirm(false)} data-testid="codes-confirm-cancel"
                      className="text-xs uppercase tracking-[0.15em] text-[#A19D94] hover:text-white transition-colors">Cancel</button>
                  </span>
                )}
              </div>
            </div>

            {early.length === 0 ? (
              <div className="border border-white/10 bg-black/40 py-16 text-center" data-testid="early-empty-state">
                <Zap className="w-8 h-8 text-[#8C7324] mx-auto mb-4" />
                <p className="text-[#A19D94]">No early access signups yet. They'll appear here as the teaser page collects them.</p>
              </div>
            ) : (
              <div className="space-y-4" data-testid="early-list">
                {early.map((c) => (
                  <div key={c.id} data-testid={`early-row-${c.id}`}
                    className="border border-white/10 bg-black/40 p-6 hover:border-[#D4AF37]/40 transition-colors duration-300">
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                      <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-sm">
                        <span className="inline-flex items-center gap-1 text-[#FDFBF7]"><Mail className="w-3.5 h-3.5 text-[#8C7324]" />{c.email}</span>
                        {c.phone && <span className="inline-flex items-center gap-1 text-[#A19D94]"><Phone className="w-3.5 h-3.5 text-[#8C7324]" />{c.phone}</span>}
                        {c.code && (
                          <span className={`inline-flex items-center gap-1 text-[11px] uppercase tracking-[0.15em] border px-2.5 py-1 ${
                            c.code_used ? "border-green-500/40 text-green-400" : "border-[#D4AF37]/40 text-[#F4D77E]"}`}
                            data-testid={`code-${c.id}`}>
                            {c.code} · {c.code_used ? "used" : c.code_sent ? "sent" : "ready"}
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-4 shrink-0">
                        <span className="text-xs uppercase tracking-[0.15em] text-[#5c5850]">
                          {new Date(c.created_at).toLocaleDateString(undefined, { month: "short", day: "numeric" })}
                        </span>
                        <button onClick={() => removeEarly(c.id)} data-testid={`delete-early-${c.id}`}
                          className="text-[#5c5850] hover:text-red-400 transition-colors" aria-label="Delete">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {tab === "codes" && promoData && (
          <div data-testid="section-codes">
            <SectionHead icon={Percent} title="Discount Codes"
              sub="Codes apply to tickets only (Oct 4, Halloween, bundle) — sections and hookah always stay full price. Only codes listed below and switched ON will work at checkout; any other code gets rejected." />

            <div className="border border-[#D4AF37]/25 bg-black/40 p-6 mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4" data-testid="promo-master">
              <div>
                <span className="text-xs uppercase tracking-[0.25em] text-[#D4AF37] block mb-2">Master Switch</span>
                <p className="text-sm text-[#A19D94]" data-testid="promo-master-status">
                  {promoData.enabled
                    ? "Codes are ON — active codes below work at checkout."
                    : "Codes are OFF — every discount code is rejected at checkout, even active ones."}
                </p>
              </div>
              <button onClick={togglePromos} disabled={promoBusy === "master"} data-testid="promo-master-toggle"
                className={`shrink-0 inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] border px-6 py-3 transition-colors duration-300 disabled:opacity-50 ${
                  promoData.enabled
                    ? "border-red-400/60 text-red-300 hover:bg-red-400 hover:text-black"
                    : "border-[#D4AF37] bg-[#D4AF37] text-black hover:bg-transparent hover:text-[#D4AF37]"}`}>
                <Power className="w-4 h-4" /> {promoBusy === "master" ? "Saving…" : promoData.enabled ? "Turn All Codes Off" : "Turn Codes On"}
              </button>
            </div>

            <div className="border border-white/10 bg-black/40 p-6 mb-8" data-testid="promo-generator">
              <span className="text-xs uppercase tracking-[0.25em] text-[#D4AF37] block mb-4">Generate a Code</span>
              <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-3">
                <label className="block">
                  <span className="text-[11px] uppercase tracking-[0.2em] text-[#A19D94]">Code</span>
                  <input value={promoForm.code} onChange={(e) => setPromoForm((f) => ({ ...f, code: e.target.value }))}
                    placeholder="Blank = auto" data-testid="promo-code-input"
                    className="mt-2 w-full bg-black border border-white/20 px-4 py-3 text-[#FDFBF7] placeholder-[#5c5850] focus:outline-none focus:border-[#D4AF37] uppercase" />
                </label>
                <label className="block">
                  <span className="text-[11px] uppercase tracking-[0.2em] text-[#A19D94]">Discount type</span>
                  <select value={promoForm.kind} onChange={(e) => setPromoForm((f) => ({ ...f, kind: e.target.value, value: "" }))} data-testid="promo-kind-select"
                    className="mt-2 w-full bg-black border border-white/20 px-4 py-3 text-[#FDFBF7] focus:outline-none focus:border-[#D4AF37]">
                    <option value="percent">Percent off</option>
                    <option value="amount">Dollars off</option>
                  </select>
                </label>
                <label className="block">
                  <span className="text-[11px] uppercase tracking-[0.2em] text-[#A19D94]">{promoForm.kind === "percent" ? "Percent (1–100)" : "Dollars"}</span>
                  <input value={promoForm.value} onChange={(e) => setPromoForm((f) => ({ ...f, value: e.target.value }))}
                    type="number" min="1" placeholder={promoForm.kind === "percent" ? "e.g. 20" : "e.g. 5"} data-testid="promo-value-input"
                    className="mt-2 w-full bg-black border border-white/20 px-4 py-3 text-[#FDFBF7] placeholder-[#5c5850] focus:outline-none focus:border-[#D4AF37]" />
                </label>
                <label className="block">
                  <span className="text-[11px] uppercase tracking-[0.2em] text-[#A19D94]">Uses</span>
                  <select value={promoForm.uses} onChange={(e) => setPromoForm((f) => ({ ...f, uses: e.target.value }))} data-testid="promo-uses-select"
                    className="mt-2 w-full bg-black border border-white/20 px-4 py-3 text-[#FDFBF7] focus:outline-none focus:border-[#D4AF37]">
                    <option value="single">Single use</option>
                    <option value="unlimited">Unlimited</option>
                  </select>
                </label>
                <label className="block">
                  <span className="text-[11px] uppercase tracking-[0.2em] text-[#A19D94]">Note (optional)</span>
                  <input value={promoForm.note} onChange={(e) => setPromoForm((f) => ({ ...f, note: e.target.value }))}
                    placeholder="e.g. DJ Bounce comp" data-testid="promo-note-input"
                    className="mt-2 w-full bg-black border border-white/20 px-4 py-3 text-[#FDFBF7] placeholder-[#5c5850] focus:outline-none focus:border-[#D4AF37]" />
                </label>
              </div>
              <button onClick={createPromo} disabled={promoBusy === "create"} data-testid="promo-generate-button"
                className="mt-5 inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] border border-[#D4AF37] bg-[#D4AF37] text-black px-6 py-3 hover:bg-transparent hover:text-[#D4AF37] transition-colors duration-300 disabled:opacity-50">
                <Percent className="w-4 h-4" /> {promoBusy === "create" ? "Creating…" : "Generate Code"}
              </button>
              <p className="text-[11px] text-[#5c5850] mt-3">Tip: 100% off = free comp ticket — the guest skips payment and gets their ticket + door QR by email instantly. Comped tickets still count toward the event's ticket cap.</p>
            </div>

            {promoData.codes.length === 0 ? (
              <div className="border border-white/10 bg-black/40 py-16 text-center" data-testid="promo-empty-state">
                <Percent className="w-8 h-8 text-[#8C7324] mx-auto mb-4" />
                <p className="text-[#A19D94]">No discount codes yet. Generate one above — until then, every code entered at checkout is rejected.</p>
              </div>
            ) : (
              <div className="space-y-4" data-testid="promo-list">
                {promoData.codes.map((c) => (
                  <div key={c.id} data-testid={`promo-row-${c.code}`}
                    className={`border bg-black/40 p-6 transition-colors duration-300 ${c.active ? "border-white/10 hover:border-[#D4AF37]/40" : "border-white/5 opacity-50"}`}>
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                      <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-sm">
                        <span className="font-serif-display text-xl text-[#F4D77E] tracking-wider" data-testid={`promo-code-${c.code}`}>{c.code}</span>
                        <span className="text-[#FDFBF7]">
                          {c.kind === "percent" && c.percent_off === 100
                            ? "Free comp ticket"
                            : c.kind === "percent" ? `${c.percent_off}% off` : `$${(c.amount_off / 100).toFixed(2)} off`}
                        </span>
                        <span className="text-xs text-[#A19D94] tabular-nums" data-testid={`promo-uses-${c.code}`}>
                          {c.times_redeemed}{c.max_redemptions ? ` / ${c.max_redemptions}` : ""} used{c.max_redemptions ? "" : " · unlimited"}
                        </span>
                        {c.note && <span className="text-xs text-[#5c5850]">{c.note}</span>}
                        <span className={`text-[11px] uppercase tracking-[0.15em] border px-2.5 py-1 ${
                          c.active && promoData.enabled ? "border-green-500/40 text-green-400" : "border-white/15 text-[#A19D94]"}`}
                          data-testid={`promo-status-${c.code}`}>
                          {c.active ? (promoData.enabled ? "live" : "on · master off") : "off"}
                        </span>
                      </div>
                      <button onClick={() => togglePromoCode(c)} disabled={promoBusy === c.id} data-testid={`promo-toggle-${c.code}`}
                        className={`shrink-0 text-xs uppercase tracking-[0.15em] border px-4 py-2 transition-colors duration-300 disabled:opacity-50 ${
                          c.active
                            ? "border-red-400/50 text-red-300 hover:bg-red-400 hover:text-black"
                            : "border-[#D4AF37]/60 text-[#D4AF37] hover:bg-[#D4AF37] hover:text-black"}`}>
                        {promoBusy === c.id ? "…" : c.active ? "Turn Off" : "Turn On"}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {tab === "contest" && (
          <div data-testid="section-contest">
            <div className="flex items-start justify-between gap-4 mb-8">
              <SectionHead icon={Trophy} title="Costume Contest · $1,000"
                sub={<span>Halloween pre-registrations. <span data-testid="contest-count">{contest.length} entered</span></span>} />
              <button onClick={exportContestCsv} data-testid="export-contest-csv-button"
                className="shrink-0 inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] border border-[#D4AF37]/60 text-[#D4AF37] px-5 py-3 hover:bg-[#D4AF37] hover:text-black transition-colors duration-300">
                <Download className="w-4 h-4" /> <span className="hidden sm:inline">Export CSV</span>
              </button>
            </div>

            {contest.length === 0 ? (
              <div className="border border-white/10 bg-black/40 py-16 text-center" data-testid="contest-empty-state">
                <Trophy className="w-8 h-8 text-[#8C7324] mx-auto mb-4" />
                <p className="text-[#A19D94]">No contest entries yet. Halloween pre-registrations will appear here.</p>
              </div>
            ) : (
              <div className="space-y-4" data-testid="contest-list">
                {contest.map((c) => (
                  <div key={c.id} data-testid={`contest-row-${c.id}`}
                    className="border border-white/10 bg-black/40 p-6 hover:border-[#D4AF37]/40 transition-colors duration-300">
                    <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                      <div>
                        <span className="font-serif-display text-2xl text-[#FDFBF7]">{c.name}</span>
                        {c.costume_idea && <p className="text-sm text-[#A19D94] italic mt-1">"{c.costume_idea}"</p>}
                        <div className="flex flex-wrap items-center gap-x-5 gap-y-2 mt-3 text-sm">
                          <span className="inline-flex items-center gap-1 text-[#A19D94]"><Mail className="w-3.5 h-3.5 text-[#8C7324]" />{c.email}</span>
                          {c.phone && <span className="inline-flex items-center gap-1 text-[#A19D94]"><Phone className="w-3.5 h-3.5 text-[#8C7324]" />{c.phone}</span>}
                        </div>
                      </div>
                      <div className="flex items-center gap-4 shrink-0">
                        <span className="text-xs uppercase tracking-[0.15em] text-[#5c5850]">
                          {new Date(c.created_at).toLocaleDateString(undefined, { month: "short", day: "numeric" })}
                        </span>
                        <button onClick={() => removeContest(c.id)} data-testid={`delete-contest-${c.id}`}
                          className="text-[#5c5850] hover:text-red-400 transition-colors" aria-label="Delete">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {tab === "blast" && (
          <div data-testid="section-blast">
            <SectionHead icon={Send} title="Email Blast"
              sub="Write your own subject and body — add images and links, size them your way, preview exactly what lands in inboxes. Past campaigns show open counts below." />
            <div className="border border-white/10 bg-black/40 p-6" data-testid="blast-section">
              <div className="flex flex-wrap gap-3 mb-6" data-testid="blast-audiences">
                {[["everyone", "Everyone"], ["early", "Early Access"], ["buyers", "Ticket Buyers"], ["section", "Section Holders"], ["requests", "Song Requests"], ["contest", "Contest Entries"]].map(([key, label]) => (
                  <button key={key} onClick={() => { setBlastAudience(key); setBlastHtml(null); setBlastConfirm(false); }} data-testid={`blast-audience-${key}`}
                    className={`text-xs uppercase tracking-[0.18em] border px-5 py-3 transition-colors duration-300 ${
                      blastAudience === key ? "border-[#D4AF37] bg-[#D4AF37]/10 text-[#F4D77E]" : "border-white/15 text-[#A19D94] hover:border-[#D4AF37]/40"}`}>
                    {label} · {audiences?.[key] ?? "…"}
                  </button>
                ))}
              </div>

              <div className="space-y-5">
                <input data-testid="blast-subject" placeholder="Subject line — your words, nothing preset" value={blastSubject}
                  onChange={(e) => { setBlastSubject(e.target.value); setBlastHtml(null); }} className={eventInputCls} />
                <div>
                  <div className="flex flex-wrap items-center gap-2 mb-2" data-testid="blast-toolbar">
                    <button type="button" onClick={insertLink} data-testid="blast-insert-link"
                      className="text-[10px] uppercase tracking-[0.15em] border border-white/20 text-[#A19D94] px-4 py-2 hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors">
                      + Link
                    </button>
                    <button type="button" onClick={() => blastImgInputRef.current?.click()} disabled={blastBusy === "image"} data-testid="blast-insert-image"
                      className="text-[10px] uppercase tracking-[0.15em] border border-white/20 text-[#A19D94] px-4 py-2 hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors disabled:opacity-50">
                      {blastBusy === "image" ? "Uploading…" : "+ Image"}
                    </button>
                    <input ref={blastImgInputRef} type="file" accept="image/jpeg,image/png,image/webp" className="hidden"
                      onChange={(e) => uploadBlastImage(e.target.files?.[0])} data-testid="blast-image-file" />
                    <select value={imgSize} onChange={(e) => setImgSize(e.target.value)} data-testid="blast-image-size"
                      className="bg-black border border-white/20 text-[#A19D94] text-[10px] uppercase tracking-[0.12em] px-3 py-2 focus:outline-none focus:border-[#D4AF37]">
                      <option value="100">Full width</option>
                      <option value="75">75% size</option>
                      <option value="50">50% size</option>
                      <option value="25">25% size</option>
                    </select>
                    <select value={imgAlign} onChange={(e) => setImgAlign(e.target.value)} data-testid="blast-image-align"
                      className="bg-black border border-white/20 text-[#A19D94] text-[10px] uppercase tracking-[0.12em] px-3 py-2 focus:outline-none focus:border-[#D4AF37]">
                      <option value="center">Centered</option>
                      <option value="left">Left</option>
                      <option value="right">Right</option>
                    </select>
                  </div>
                  <textarea ref={blastBodyRef} data-testid="blast-message" rows={8} value={blastMessage}
                    placeholder={"Write your email body here. Hit Enter for new lines.\nUse + Link and + Image to drop them right where your cursor is."}
                    onChange={(e) => { setBlastMessage(e.target.value); setBlastHtml(null); }}
                    className={`${eventInputCls} border border-white/20 p-4 resize-none font-mono text-xs leading-relaxed`} />
                  <p className="text-[10px] text-[#5c5850] mt-2">Plain text works as-is; images and links insert as small HTML tags — that's normal. Preview shows the real email.</p>
                </div>
              </div>

              <div className="flex flex-wrap items-center gap-3 mt-6">
                <button onClick={previewBlast} disabled={!!blastBusy} data-testid="blast-preview-button"
                  className="text-xs uppercase tracking-[0.2em] border border-white/25 text-[#FDFBF7] px-6 py-3 hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors duration-300 disabled:opacity-50">
                  {blastBusy === "preview" ? "Loading…" : "Preview Email"}
                </button>
                <button onClick={() => sendBlast(true)} disabled={!!blastBusy} data-testid="blast-test-button"
                  className="text-xs uppercase tracking-[0.2em] border border-white/25 text-[#A19D94] px-6 py-3 hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors duration-300 disabled:opacity-50">
                  {blastBusy === "test" ? "Sending…" : "Send Test to Myself"}
                </button>
                {!blastConfirm ? (
                  <button onClick={() => setBlastConfirm(true)} disabled={!!blastBusy} data-testid="blast-send-button"
                    className="text-xs uppercase tracking-[0.2em] border border-[#D4AF37] bg-[#D4AF37] text-black px-6 py-3 hover:bg-transparent hover:text-[#D4AF37] transition-colors duration-300 disabled:opacity-50">
                    Send Blast
                  </button>
                ) : (
                  <span className="inline-flex items-center gap-3 border border-red-400/50 bg-red-950/30 px-5 py-3" data-testid="blast-confirm">
                    <span className="text-xs uppercase tracking-[0.15em] text-red-300">Send to {audiences?.[blastAudience] ?? "?"} people?</span>
                    <button onClick={() => sendBlast(false)} disabled={!!blastBusy} data-testid="blast-confirm-send"
                      className="text-xs uppercase tracking-[0.15em] text-[#F4D77E] hover:text-white transition-colors">
                      {blastBusy === "send" ? "Sending…" : "Yes, Send"}
                    </button>
                    <button onClick={() => setBlastConfirm(false)} data-testid="blast-confirm-cancel"
                      className="text-xs uppercase tracking-[0.15em] text-[#A19D94] hover:text-white transition-colors">Cancel</button>
                  </span>
                )}
              </div>
            </div>

            <div className="border border-white/10 bg-black/40 p-6 mt-6" data-testid="campaign-history">
              <span className="text-xs uppercase tracking-[0.25em] text-[#D4AF37] block mb-2">Past Campaigns</span>
              <p className="text-sm text-[#A19D94] mb-6">Every blast you've sent, with live open counts — opens tick up as recipients read the email.</p>
              {campaigns.length === 0 && <p className="text-sm text-[#5c5850]" data-testid="campaigns-empty">No campaigns sent yet — your first blast will show up here.</p>}
              <div className="space-y-3">
                {campaigns.map((c) => (
                  <div key={c.id} className="flex flex-wrap items-center justify-between gap-3 border border-white/10 bg-black/30 px-5 py-4" data-testid={`campaign-${c.id}`}>
                    <div className="min-w-0">
                      <div className="text-sm text-[#FDFBF7] truncate">{c.subject}</div>
                      <div className="text-[11px] uppercase tracking-[0.15em] text-[#A19D94] mt-1">
                        {c.audience} · {new Date(c.created_at).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })} · {c.sent}/{c.total} delivered
                      </div>
                    </div>
                    <div className="text-right shrink-0" data-testid={`campaign-opens-${c.id}`}>
                      <div className="font-serif-display text-2xl text-[#F4D77E]">{c.opens || 0}</div>
                      <div className="text-[10px] uppercase tracking-[0.2em] text-[#8C7324]">
                        opens{c.sent ? ` · ${Math.round(((c.opens || 0) / c.sent) * 100)}%` : ""}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="border border-white/10 bg-black/40 p-6 mt-6" data-testid="schedule-section">
              <span className="text-xs uppercase tracking-[0.25em] text-[#D4AF37] block mb-2">Scheduled Reminders</span>
              <p className="text-sm text-[#A19D94] mb-6">Queue a day-before reminder for any event — it emails everyone automatically at that time the day before the event, even if you never open this page.</p>

              <div className="grid md:grid-cols-2 gap-x-8 gap-y-5">
                <label className="block">
                  <span className="text-[11px] uppercase tracking-[0.2em] text-[#A19D94]">Event</span>
                  <select data-testid="schedule-event-select" value={schedEvent} onChange={(e) => pickSchedEvent(e.target.value)}
                    className="mt-2 w-full bg-black border border-white/20 px-4 py-3 text-[#FDFBF7] focus:outline-none focus:border-[#D4AF37]">
                    <option value="">Choose an event…</option>
                    {eventList.filter((e) => e.event_datetime && e.status !== "past").map((e) => (
                      <option key={e.slug} value={e.slug}>{e.title} · {e.date_label}</option>
                    ))}
                  </select>
                </label>
                <label className="block">
                  <span className="text-[11px] uppercase tracking-[0.2em] text-[#A19D94]">Subject</span>
                  <input data-testid="schedule-subject" value={schedSubject} onChange={(e) => setSchedSubject(e.target.value)}
                    className={`${eventInputCls} mt-2`} placeholder="Tomorrow night: …" />
                </label>
                <textarea data-testid="schedule-message" rows={4} value={schedMessage} placeholder="Reminder message…"
                  onChange={(e) => setSchedMessage(e.target.value)}
                  className={`${eventInputCls} md:col-span-2 border border-white/20 p-4 resize-none`} />
              </div>
              <button onClick={scheduleBlast} disabled={schedBusy || !schedEvent} data-testid="schedule-button"
                className="mt-5 text-xs uppercase tracking-[0.2em] border border-[#D4AF37] bg-[#D4AF37] text-black px-8 py-3 hover:bg-transparent hover:text-[#D4AF37] transition-colors duration-300 disabled:opacity-50">
                {schedBusy ? "Scheduling…" : "Schedule Day-Before Reminder"}
              </button>

              {scheduled.length > 0 && (
                <div className="mt-8 space-y-3" data-testid="scheduled-list">
                  {scheduled.map((b) => (
                    <div key={b.id} className="flex flex-wrap items-center justify-between gap-3 border border-white/10 bg-black/30 px-5 py-4" data-testid={`scheduled-blast-${b.id}`}>
                      <div className="min-w-0">
                        <div className="text-sm text-[#FDFBF7] truncate">{b.subject}</div>
                        <div className="text-[11px] uppercase tracking-[0.15em] text-[#A19D94] mt-1">
                          {b.event_title} · sends {new Date(b.send_at).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}
                          {b.status === "sent" ? ` · Sent (${b.sent}/${b.total})` : b.status === "sending" ? " · Sending…" : ""}
                        </div>
                      </div>
                      {b.status === "pending" ? (
                        <span className="inline-flex items-center gap-3">
                          <span className="text-[10px] uppercase tracking-[0.2em] text-[#F4D77E] border border-[#D4AF37]/40 px-3 py-1.5">Scheduled</span>
                          <button onClick={() => cancelBlast(b.id)} data-testid={`cancel-blast-${b.id}`}
                            className="text-[#5c5850] hover:text-red-400 transition-colors" aria-label="Cancel reminder">
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </span>
                      ) : (
                        <span className="text-[10px] uppercase tracking-[0.2em] text-green-400 border border-green-400/30 px-3 py-1.5" data-testid={`blast-status-${b.id}`}>{b.status}</span>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {tab === "sections" && (
          <div data-testid="section-sections">
            <SectionHead icon={Armchair} title="Sections & Bottle Service"
              sub={sectionEvent === "ep3-nov28"
                ? "Chocolate City (Nov 28) — build your section layout and prices below; new reservations land here at the forefront."
                : "Oct 4 · Pretty Girls Love R&B — past reservations archive."} />

            <div className="flex flex-wrap gap-3 mb-6" data-testid="section-event-tabs">
              {[["ep3-nov28", "Chocolate City · Nov 28"], ["pglr-oct4", "Oct 4 · Pretty Girls (Archive)"]].map(([key, label]) => (
                <button key={key} onClick={() => { setSectionEvent(key); loadSections(key); }} data-testid={`section-event-tab-${key}`}
                  className={`text-xs uppercase tracking-[0.18em] border px-5 py-3 transition-colors duration-300 ${
                    sectionEvent === key ? "border-[#D4AF37] bg-[#D4AF37]/10 text-[#F4D77E]" : "border-white/15 text-[#A19D94] hover:border-[#D4AF37]/40"}`}>
                  {label}
                </button>
              ))}
            </div>

            {sectionEvent === "ep3-nov28" && (
              <div className="border border-white/10 bg-black/40 p-6 mb-6" data-testid="tier-manager">
                <span className="text-xs uppercase tracking-[0.25em] text-[#D4AF37] block mb-2">Chocolate City Layout — Add A Section</span>
                <p className="text-sm text-[#A19D94] mb-5">Name it, price the deposit, set how many exist. It goes live on the site's sections page instantly.</p>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  <label className="block">
                    <span className="text-[11px] uppercase tracking-[0.2em] text-[#A19D94]">Section name</span>
                    <input value={tierForm.name} onChange={(e) => setTierForm((f) => ({ ...f, name: e.target.value }))}
                      placeholder="e.g. Cocoa Cabana" className={`${eventInputCls} mt-2`} data-testid="tier-name" />
                  </label>
                  <label className="block">
                    <span className="text-[11px] uppercase tracking-[0.2em] text-[#A19D94]">Location / area</span>
                    <input value={tierForm.area} onChange={(e) => setTierForm((f) => ({ ...f, area: e.target.value }))}
                      placeholder="e.g. Main floor — by the DJ" className={`${eventInputCls} mt-2`} data-testid="tier-area" />
                  </label>
                  <label className="block">
                    <span className="text-[11px] uppercase tracking-[0.2em] text-[#A19D94]">Deposit price ($)</span>
                    <input type="number" min="0" step="1" value={tierForm.deposit} onChange={(e) => setTierForm((f) => ({ ...f, deposit: e.target.value }))}
                      className={`${eventInputCls} mt-2`} data-testid="tier-deposit" />
                  </label>
                  <label className="block">
                    <span className="text-[11px] uppercase tracking-[0.2em] text-[#A19D94]">Max guests</span>
                    <input type="number" min="1" max="30" value={tierForm.guests} onChange={(e) => setTierForm((f) => ({ ...f, guests: e.target.value }))}
                      className={`${eventInputCls} mt-2`} data-testid="tier-guests" />
                  </label>
                  <label className="block">
                    <span className="text-[11px] uppercase tracking-[0.2em] text-[#A19D94]">Bottle minimum</span>
                    <input type="number" min="0" max="10" value={tierForm.min_bottles} onChange={(e) => setTierForm((f) => ({ ...f, min_bottles: e.target.value }))}
                      className={`${eventInputCls} mt-2`} data-testid="tier-min-bottles" />
                  </label>
                  <label className="block">
                    <span className="text-[11px] uppercase tracking-[0.2em] text-[#A19D94]">How many exist</span>
                    <input type="number" min="1" max="50" value={tierForm.capacity} onChange={(e) => setTierForm((f) => ({ ...f, capacity: e.target.value }))}
                      className={`${eventInputCls} mt-2`} data-testid="tier-capacity" />
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer select-none self-end pb-2">
                    <input type="checkbox" checked={tierForm.per_guest} onChange={(e) => setTierForm((f) => ({ ...f, per_guest: e.target.checked }))}
                      className="w-4 h-4 accent-[#D4AF37]" data-testid="tier-per-guest" />
                    <span className="text-[11px] uppercase tracking-[0.2em] text-[#A19D94]">Price is per guest</span>
                  </label>
                </div>
                <button onClick={addTier} disabled={tierBusy === "add"} data-testid="tier-add"
                  className="mt-5 text-xs uppercase tracking-[0.2em] border border-[#D4AF37] bg-[#D4AF37] text-black px-8 py-3 hover:bg-transparent hover:text-[#D4AF37] transition-colors duration-300 disabled:opacity-50">
                  {tierBusy === "add" ? "Adding…" : "Add Section To Layout"}
                </button>

                {(sectionsData?.sections || []).length > 0 && (
                  <div className="mt-6 space-y-2" data-testid="tier-list">
                    {sectionsData.sections.map((t) => (
                      <div key={t.id} className="flex flex-wrap items-center justify-between gap-3 border border-white/10 bg-black/30 px-4 py-3" data-testid={`tier-row-${t.id}`}>
                        <div className="min-w-0">
                          <span className="text-sm text-[#FDFBF7]">{t.name}</span>
                          <span className="text-[11px] text-[#8C7324] ml-3">${(t.deposit / 100).toFixed(0)} deposit · {t.guests} guests · {t.capacity} available{t.min_bottles ? ` · ${t.min_bottles} bottle min` : ""}</span>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          <button onClick={() => patchTier(t.id, { active: !t.active })} disabled={tierBusy === t.id} data-testid={`tier-toggle-${t.id}`}
                            className={`text-[10px] uppercase tracking-[0.15em] border px-3 py-1.5 transition-colors ${
                              t.active ? "border-green-400/40 text-green-300" : "border-white/20 text-[#5c5850]"}`}>
                            {t.active ? "Live" : "Hidden"}
                          </button>
                          <button onClick={() => deleteTier(t.id)} disabled={tierBusy === t.id} data-testid={`tier-delete-${t.id}`}
                            className="text-[#5c5850] hover:text-red-400 transition-colors" aria-label="Delete section">
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            <div className="border border-white/10 bg-black/40 p-6 mb-6 flex flex-wrap items-center justify-between gap-4" data-testid="sections-toggle-row">
              <p className="text-sm text-[#A19D94]">
                Sections page is <span className={sectionsData?.enabled ? "text-green-300" : "text-red-300"} data-testid="sections-state">{sectionsData?.enabled ? "LIVE on the site" : "HIDDEN from the site"}</span>
                {sectionsData?.sold_out && <span className="text-red-300" data-testid="sections-soldout-state"> · showing SOLD OUT, new reservations blocked</span>}
              </p>
              <div className="flex flex-wrap gap-2">
                <button onClick={toggleSoldOut} disabled={sectionsBusy === "soldout"} data-testid="sections-soldout-button"
                  className={`text-xs uppercase tracking-[0.2em] border px-6 py-3 transition-colors duration-300 disabled:opacity-50 ${
                    sectionsData?.sold_out
                      ? "border-[#D4AF37] bg-[#D4AF37] text-black hover:bg-transparent hover:text-[#D4AF37]"
                      : "border-red-400/50 text-red-300 hover:bg-red-400/10"}`}>
                  {sectionsBusy === "soldout" ? "…" : sectionsData?.sold_out ? "Reopen Sections" : "Mark All Sold Out"}
                </button>
                <button onClick={toggleSections} disabled={sectionsBusy === "toggle"} data-testid="sections-toggle-button"
                  className={`text-xs uppercase tracking-[0.2em] border px-6 py-3 transition-colors duration-300 disabled:opacity-50 ${
                    sectionsData?.enabled
                      ? "border-red-400/50 text-red-300 hover:bg-red-400/10"
                      : "border-[#D4AF37] bg-[#D4AF37] text-black hover:bg-transparent hover:text-[#D4AF37]"}`}>
                  {sectionsBusy === "toggle" ? "…" : sectionsData?.enabled ? "Hide Sections" : "Show Sections"}
                </button>
              </div>
            </div>

            {sectionEvent === "ep3-nov28" && (
              <div className="border border-[#D4AF37]/25 bg-black/40 p-6 mb-6" data-testid="manual-book-card">
                <span className="text-xs uppercase tracking-[0.25em] text-[#D4AF37] block mb-2">Book A Section Manually</span>
                <p className="text-sm text-[#A19D94] mb-5">For guests who paid you directly — cash, Cash App, Apple Pay, Zelle. Log it here so the section shows reserved on the site, and they'll get the same receipt and confirmation email as online bookings. If the section is already taken, this will refuse — no double bookings.</p>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  <label className="block">
                    <span className="text-[11px] uppercase tracking-[0.2em] text-[#A19D94]">Section</span>
                    <select value={manualForm.section_id} onChange={(e) => setManualForm((f) => ({ ...f, section_id: e.target.value }))}
                      className={`${eventInputCls} mt-2`} data-testid="manual-section">
                      <option value="">— choose —</option>
                      {(sectionsData?.sections || []).filter((s) => s.active && s.remaining > 0).map((s) => (
                        <option key={s.id} value={s.id}>{s.name} · ${(s.deposit / 100).toFixed(0)}{s.per_guest ? "/guest" : ""} · {s.remaining} left</option>
                      ))}
                    </select>
                  </label>
                  <label className="block">
                    <span className="text-[11px] uppercase tracking-[0.2em] text-[#A19D94]">Guest name</span>
                    <input value={manualForm.name} onChange={(e) => setManualForm((f) => ({ ...f, name: e.target.value }))}
                      placeholder="Full name" className={`${eventInputCls} mt-2`} data-testid="manual-name" />
                  </label>
                  <label className="block">
                    <span className="text-[11px] uppercase tracking-[0.2em] text-[#A19D94]">Guest email</span>
                    <input type="email" value={manualForm.email} onChange={(e) => setManualForm((f) => ({ ...f, email: e.target.value }))}
                      placeholder="for the receipt" className={`${eventInputCls} mt-2`} data-testid="manual-email" />
                  </label>
                  <label className="block">
                    <span className="text-[11px] uppercase tracking-[0.2em] text-[#A19D94]">Phone (for SMS confirmation)</span>
                    <input value={manualForm.phone} onChange={(e) => setManualForm((f) => ({ ...f, phone: e.target.value }))}
                      placeholder="optional" className={`${eventInputCls} mt-2`} data-testid="manual-phone" />
                  </label>
                  <label className="block">
                    <span className="text-[11px] uppercase tracking-[0.2em] text-[#A19D94]">Party size</span>
                    <input type="number" min="1" max="30" value={manualForm.party_size} onChange={(e) => setManualForm((f) => ({ ...f, party_size: e.target.value }))}
                      className={`${eventInputCls} mt-2`} data-testid="manual-party" />
                  </label>
                  <label className="block">
                    <span className="text-[11px] uppercase tracking-[0.2em] text-[#A19D94]">How they paid</span>
                    <select value={manualForm.pay_method} onChange={(e) => setManualForm((f) => ({ ...f, pay_method: e.target.value }))}
                      className={`${eventInputCls} mt-2`} data-testid="manual-pay-method">
                      {["Cash", "Cash App", "Apple Pay", "Zelle", "Venmo", "Other"].map((m) => <option key={m} value={m}>{m}</option>)}
                    </select>
                  </label>
                  <label className="block">
                    <span className="text-[11px] uppercase tracking-[0.2em] text-[#A19D94]">Deposit received ($)</span>
                    <input type="number" min="0" step="1" value={manualForm.deposit} onChange={(e) => setManualForm((f) => ({ ...f, deposit: e.target.value }))}
                      placeholder="e.g. 100" className={`${eventInputCls} mt-2`} data-testid="manual-deposit" />
                  </label>
                  <label className="block">
                    <span className="text-[11px] uppercase tracking-[0.2em] text-[#A19D94]">Balance still due ($)</span>
                    <input type="number" min="0" step="1" value={manualForm.balance} onChange={(e) => setManualForm((f) => ({ ...f, balance: e.target.value }))}
                      className={`${eventInputCls} mt-2`} data-testid="manual-balance" />
                  </label>
                  <label className="block">
                    <span className="text-[11px] uppercase tracking-[0.2em] text-[#A19D94]">Note (only you see this)</span>
                    <input value={manualForm.note} onChange={(e) => setManualForm((f) => ({ ...f, note: e.target.value }))}
                      placeholder="e.g. sender's Cash App name" className={`${eventInputCls} mt-2`} data-testid="manual-note" />
                  </label>
                </div>
                <div className="flex flex-wrap items-center gap-4 mt-5">
                  <label className="flex items-center gap-2 cursor-pointer select-none">
                    <input type="checkbox" checked={manualForm.send_receipt} onChange={(e) => setManualForm((f) => ({ ...f, send_receipt: e.target.checked }))}
                      className="w-4 h-4 accent-[#D4AF37]" data-testid="manual-send-receipt" />
                    <span className="text-[11px] uppercase tracking-[0.2em] text-[#A19D94]">Email receipt + SMS confirmation</span>
                  </label>
                  <button onClick={manualBook} disabled={manualBusy} data-testid="manual-book-button"
                    className="text-xs uppercase tracking-[0.2em] border border-[#D4AF37] bg-[#D4AF37] text-black px-8 py-3 hover:bg-transparent hover:text-[#D4AF37] transition-colors duration-300 disabled:opacity-50">
                    {manualBusy ? "Booking…" : "Book Section & Send Receipt"}
                  </button>
                </div>
              </div>
            )}

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8" data-testid="sections-availability">
              {(sectionsData?.sections || []).map((s) => (
                <div key={s.id} className={`border p-4 ${s.remaining === 0 ? "border-red-400/30 bg-red-950/10" : "border-white/10 bg-black/40"}`} data-testid={`admin-section-${s.id}`}>
                  <div className="flex items-center justify-between">
                    <span className="font-serif-display text-lg text-[#FDFBF7]">{s.name}</span>
                  </div>
                  <div className="text-[11px] text-[#A19D94] mt-1">{s.seat ? `${s.remaining} of 10 seats left` : `${s.guests} guests`} · ${s.deposit / 100} deposit</div>
                  <div className={`text-[10px] uppercase tracking-[0.2em] mt-2 ${s.remaining === 0 ? "text-red-400" : "text-green-300"}`} data-testid={`admin-section-status-${s.id}`}>
                    {s.remaining === 0 ? "Reserved" : s.seat || s.id === "RAIL_FULL" ? `${s.remaining} available` : "Available"}
                  </div>
                </div>
              ))}
            </div>

            <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
              <div className="text-xs uppercase tracking-[0.25em] text-[#D4AF37]" data-testid="reservations-label">
                Reservations ({sectionsData?.reservations?.length || 0})
                {(sectionsData?.reservations || []).length > 0 && (
                  <span className="text-[#A19D94] normal-case tracking-normal" data-testid="reservations-settled-count">
                    {" "}· {(sectionsData?.reservations || []).filter((r) => r.balance_paid).length} settled
                  </span>
                )}
              </div>
              <div className="flex flex-wrap gap-2">
                <button onClick={sendManageLinks} disabled={sectionsBusy === "links" || !(sectionsData?.reservations || []).length} data-testid="send-manage-links"
                  className="text-[10px] uppercase tracking-[0.15em] border border-[#D4AF37] bg-[#D4AF37] text-black px-4 py-2 hover:bg-transparent hover:text-[#D4AF37] transition-colors disabled:opacity-40">
                  {sectionsBusy === "links" ? "Sending…" : "Send Thank-You + Manage Links"}
                </button>
                <button onClick={sendBalanceReminders} disabled={sectionsBusy === "remind" || !(sectionsData?.reservations || []).some((r) => !r.balance_paid)} data-testid="send-balance-reminders"
                  className="text-[10px] uppercase tracking-[0.15em] border border-[#D4AF37]/50 text-[#F4D77E] px-4 py-2 hover:bg-[#D4AF37]/10 transition-colors disabled:opacity-40">
                  {sectionsBusy === "remind" ? "Sending…" : "Send Balance Reminders"}
                </button>
              </div>
            </div>
            {!(sectionsData?.reservations || []).length && (
              <p className="text-sm text-[#A19D94]" data-testid="reservations-empty">No paid reservations yet.</p>
            )}
            <div className="space-y-3">
              {(sectionsData?.reservations || []).map((r) => (
                <div key={r.session_id} className="border border-white/10 bg-black/40 p-5 flex flex-wrap items-center justify-between gap-4" data-testid={`reservation-${r.session_id}`}>
                  <div>
                    <div className="text-[#FDFBF7] font-semibold flex items-center gap-3 flex-wrap">
                      {r.section_name} <span className="text-[#8C7324] font-normal">— {r.name}, party of {r.party_size}</span>
                      {r.balance_paid && (
                        <span className="text-[9px] uppercase tracking-[0.2em] border border-[#D4AF37] bg-[#D4AF37]/15 text-[#F4D77E] px-2.5 py-1" data-testid={`settled-badge-${r.session_id}`}>
                          Settled
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-[#A19D94] mt-1">{r.reservation_no && <span className="text-[#F4D77E]">{r.reservation_no} · </span>}{r.email}{r.phone ? ` · ${r.phone}` : ""}{r.manual && r.pay_method ? <span className="text-[#8C7324]"> · {r.pay_method} (manual)</span> : ""}{r.manage_link_sent_at ? " · link sent" : ""}</div>
                    {r.conflict && (
                      <div className="text-[10px] uppercase tracking-[0.15em] text-red-300 border border-red-400/40 bg-red-950/30 px-3 py-1.5 mt-2 inline-block" data-testid={`conflict-badge-${r.session_id}`}>
                        Double-booking conflict — auto-refunded
                      </div>
                    )}
                    <div className="text-xs text-[#8C7324] mt-1">
                      {r.bottles.map((b) => `${b.qty}× ${b.name}`).join(", ") || "No bottles listed"}
                      {r.hookahs > 0 && <span className="text-[#A19D94]"> · {r.hookahs}× hookah ({(r.hookah_flavors || []).join(", ")})</span>}
                      {" "}· Deposit ${r.deposit_cents / 100} paid ·{" "}
                      {r.balance_paid
                        ? <span className="text-green-300" data-testid={`balance-paid-${r.session_id}`}>Balance ${r.balance_due_cents / 100} PAID{r.balance_paid_method ? ` · ${r.balance_paid_method}` : ""}{r.balance_paid_note ? ` from ${r.balance_paid_note}` : ""}</span>
                        : <span className="text-[#F4D77E]" data-testid={`balance-due-${r.session_id}`}>Balance ${r.balance_due_cents / 100} due {r.balance_due_date}{r.balance_reminded_at ? " · reminded" : ""}</span>}
                    </div>
                  </div>
                  <div className="flex flex-wrap items-center gap-2 shrink-0">
                    {!r.balance_paid && (
                      markPaidConfirm === r.session_id ? (
                        <span className="inline-flex flex-wrap items-center gap-3 border border-[#D4AF37]/50 bg-[#D4AF37]/10 px-4 py-2" data-testid={`mark-paid-confirm-${r.session_id}`}>
                          <span className="text-[10px] uppercase tracking-[0.15em] text-[#F4D77E]">Paid via</span>
                          <select value={markPaidMethod} onChange={(e) => setMarkPaidMethod(e.target.value)} data-testid={`mark-paid-method-${r.session_id}`}
                            className="bg-black border border-white/20 text-[#FDFBF7] text-xs px-2 py-1.5 focus:outline-none focus:border-[#D4AF37]">
                            {["Apple Pay", "Cash App", "Zelle", "Cash", "Venmo", "Other"].map((m) => <option key={m} value={m}>{m}</option>)}
                          </select>
                          <input value={markPaidNote} onChange={(e) => setMarkPaidNote(e.target.value)}
                            placeholder="Sender's name (e.g. Apple Pay name)" data-testid={`mark-paid-note-${r.session_id}`}
                            className="bg-black border border-white/20 text-[#FDFBF7] text-xs px-2 py-1.5 w-44 placeholder-[#5c5850] focus:outline-none focus:border-[#D4AF37]" />
                          <button onClick={() => markSectionPaid(r.session_id)} disabled={sectionsBusy === r.session_id} data-testid={`mark-paid-yes-${r.session_id}`}
                            className="text-[10px] uppercase tracking-[0.15em] text-[#F4D77E] hover:text-white transition-colors">
                            {sectionsBusy === r.session_id ? "…" : "Mark Paid + Send Receipt"}
                          </button>
                          <button onClick={() => { setMarkPaidConfirm(""); setMarkPaidNote(""); }} data-testid={`mark-paid-cancel-${r.session_id}`}
                            className="text-[10px] uppercase tracking-[0.15em] text-[#A19D94] hover:text-white transition-colors">Cancel</button>
                        </span>
                      ) : (
                        <button onClick={() => { setMarkPaidConfirm(r.session_id); setMarkPaidNote(""); setMarkPaidMethod("Apple Pay"); }} disabled={sectionsBusy === r.session_id} data-testid={`mark-paid-${r.session_id}`}
                          className="text-[10px] uppercase tracking-[0.15em] border border-green-500/50 text-green-300 px-4 py-2 hover:bg-green-500 hover:text-black transition-colors disabled:opacity-50">
                          Mark Paid + Receipt
                        </button>
                      )
                    )}
                    <button onClick={() => cancelReservation(r.session_id)} disabled={sectionsBusy === r.session_id} data-testid={`cancel-reservation-${r.session_id}`}
                      className="text-[10px] uppercase tracking-[0.15em] border border-red-400/40 text-red-300 px-4 py-2 hover:bg-red-400/10 transition-colors disabled:opacity-50">
                      {sectionsBusy === r.session_id ? "…" : "Cancel & Free Section"}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === "sms" && (
          <div data-testid="section-sms">
            <SectionHead icon={MessageSquare} title="SMS Blast"
              sub="Text the phone numbers fans left on signups, song requests, contest entries and checkout. Texts only go to people who gave a number — keep them short and sweet." />

            <div className={`border p-6 mb-6 ${smsStatus?.configured ? "border-green-500/30 bg-green-950/20" : "border-[#D4AF37]/40 bg-[#D4AF37]/5"}`} data-testid="sms-status-banner">
              {smsStatus?.configured ? (
                <p className="text-sm text-green-300">SMS is live — texts send from {smsStatus.from_number}.</p>
              ) : (
                <p className="text-sm text-[#F4D77E]" data-testid="sms-setup-note">
                  {smsStatus?.credentials_valid === false
                    ? "Your Twilio credentials were rejected — double-check the Account SID and Auth Token."
                    : "Almost there — your Twilio account is connected. Add a Twilio phone number and texting switches on automatically."}
                </p>
              )}
            </div>

            <div className="border border-white/10 bg-black/40 p-6" data-testid="sms-section">
              <div className="flex flex-wrap gap-3 mb-6" data-testid="sms-audiences">
                {[["everyone", "Everyone"], ["early", "Early Access"], ["buyers", "Ticket Buyers"], ["requests", "Song Requests"], ["contest", "Contest Entries"]].map(([key, label]) => (
                  <button key={key} onClick={() => { setSmsAudience(key); setSmsConfirm(false); }} data-testid={`sms-audience-${key}`}
                    className={`text-xs uppercase tracking-[0.18em] border px-5 py-3 transition-colors duration-300 ${
                      smsAudience === key ? "border-[#D4AF37] bg-[#D4AF37]/10 text-[#F4D77E]" : "border-white/15 text-[#A19D94] hover:border-[#D4AF37]/40"}`}>
                    {label} · {smsAudiences?.[key] ?? "…"}
                  </button>
                ))}
              </div>

              <textarea data-testid="sms-message" rows={4} value={smsMessage} placeholder="Your text…"
                onChange={(e) => { setSmsMessage(e.target.value); setSmsConfirm(false); }}
                className={`${eventInputCls} border border-white/20 p-4 resize-none`} />
              <div className="flex items-center justify-between mt-2">
                <p className="text-[11px] text-[#A19D94]">"Reply STOP to opt out." is added automatically to blasts.</p>
                <span className="text-[11px] uppercase tracking-[0.15em] text-[#A19D94]" data-testid="sms-char-count">
                  {smsMessage.length} chars · ~{Math.max(1, Math.ceil(smsMessage.length / 160))} text{Math.ceil(smsMessage.length / 160) > 1 ? "s" : ""} per person
                </span>
              </div>

              <div className="flex flex-wrap items-center gap-3 mt-6">
                <input data-testid="sms-test-phone" placeholder="Test phone (e.g. 3145551234)" value={smsTestPhone}
                  onChange={(e) => setSmsTestPhone(e.target.value)}
                  className="bg-transparent border border-white/20 px-4 py-3 text-sm text-[#FDFBF7] placeholder-[#5c5850] focus:outline-none focus:border-[#D4AF37] transition-colors w-64" />
                <button onClick={() => sendSmsBlast(true)} disabled={!!smsBusy || !smsStatus?.configured} data-testid="sms-test-button"
                  className="text-xs uppercase tracking-[0.2em] border border-white/25 text-[#A19D94] px-6 py-3 hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors duration-300 disabled:opacity-50">
                  {smsBusy === "test" ? "Sending…" : "Send Test Text"}
                </button>
                {!smsConfirm ? (
                  <button onClick={() => setSmsConfirm(true)} disabled={!!smsBusy || !smsStatus?.configured} data-testid="sms-send-button"
                    className="text-xs uppercase tracking-[0.2em] border border-[#D4AF37] bg-[#D4AF37] text-black px-6 py-3 hover:bg-transparent hover:text-[#D4AF37] transition-colors duration-300 disabled:opacity-50">
                    Send Text Blast
                  </button>
                ) : (
                  <span className="inline-flex items-center gap-3 border border-red-400/50 bg-red-950/30 px-5 py-3" data-testid="sms-confirm">
                    <span className="text-xs uppercase tracking-[0.15em] text-red-300">Text {smsAudiences?.[smsAudience] ?? "?"} phones?</span>
                    <button onClick={() => sendSmsBlast(false)} disabled={!!smsBusy} data-testid="sms-confirm-send"
                      className="text-xs uppercase tracking-[0.15em] text-[#F4D77E] hover:text-white transition-colors">
                      {smsBusy === "send" ? "Sending…" : "Yes, Send"}
                    </button>
                    <button onClick={() => setSmsConfirm(false)} data-testid="sms-confirm-cancel"
                      className="text-xs uppercase tracking-[0.15em] text-[#A19D94] hover:text-white transition-colors">Cancel</button>
                  </span>
                )}
              </div>
              {!smsStatus?.configured && (
                <p className="text-[11px] text-[#5c5850] mt-4">Sending is disabled until the Twilio phone number is added — composing and audience counts work now.</p>
              )}
            </div>
          </div>
        )}

        {tab === "hookah" && (
          <div data-testid="section-hookah-admin">
            <SectionHead icon={Wind} title="Hookah Preorders"
              sub="$40 hookah (1 flavor, up to 2 hours) · $20 repack — outdoor patio only, hookahs stay outside." />

            <div className="border border-white/10 bg-black/40 p-6 mb-6 flex flex-wrap items-end gap-4" data-testid="hookah-config-row">
              <p className="text-sm text-[#A19D94] w-full sm:w-auto sm:mr-auto">
                Preorders are <span className={hookahData?.enabled ? "text-green-300" : "text-red-300"} data-testid="hookah-state">{hookahData?.enabled ? "LIVE" : "OFF"}</span>
                {" "}· <span data-testid="hookah-sold-count">{hookahData?.sold ?? 0} sold · {hookahData?.remaining ?? 0} of {hookahData?.total ?? 20} left</span>
              </p>
              <label className="block">
                <span className="text-[10px] text-[#5c5850]">Total hookahs available</span>
                <input type="number" min="1" value={hookahTotalInput} onChange={(e) => setHookahTotalInput(e.target.value)} data-testid="hookah-total-input"
                  className="mt-1 w-28 bg-transparent border-b border-white/20 py-2 text-[#FDFBF7] text-lg focus:outline-none focus:border-[#D4AF37]" />
              </label>
              <button onClick={() => saveHookahConfig()} disabled={hookahBusy === "config"} data-testid="hookah-save-config"
                className="text-xs uppercase tracking-[0.2em] border border-[#D4AF37] bg-[#D4AF37] text-black px-6 py-3 hover:bg-transparent hover:text-[#D4AF37] transition-colors duration-300 disabled:opacity-50">
                {hookahBusy === "config" ? "Saving…" : "Save"}
              </button>
              <button onClick={() => saveHookahConfig(!hookahData?.enabled)} disabled={hookahBusy === "config"} data-testid="hookah-toggle-button"
                className={`text-xs uppercase tracking-[0.2em] border px-6 py-3 transition-colors duration-300 disabled:opacity-50 ${
                  hookahData?.enabled ? "border-red-400/50 text-red-300 hover:bg-red-400/10" : "border-[#D4AF37] text-[#F4D77E] hover:bg-[#D4AF37]/10"}`}>
                {hookahData?.enabled ? "Turn Preorders Off" : "Turn Preorders On"}
              </button>
            </div>

            <div className="text-xs uppercase tracking-[0.25em] text-[#D4AF37] mb-3" data-testid="hookah-orders-label">
              Paid preorders ({hookahData?.orders?.length || 0})
            </div>
            {!(hookahData?.orders || []).length && (
              <p className="text-sm text-[#A19D94]" data-testid="hookah-orders-empty">No hookah preorders yet.</p>
            )}
            <div className="space-y-3">
              {(hookahData?.orders || []).map((o) => (
                <div key={o.session_id} className={`border p-5 flex flex-wrap items-center justify-between gap-4 ${o.redeemed ? "border-green-500/25 bg-green-950/10" : "border-white/10 bg-black/40"}`} data-testid={`hookah-order-${o.session_id}`}>
                  <div>
                    <div className="text-[#FDFBF7] font-semibold">
                      {o.name} <span className="text-[#8C7324] font-normal">— {o.hookahs > 0 ? `${o.hookahs}× hookah` : ""}{o.hookahs > 0 && o.repacks > 0 ? " + " : ""}{o.repacks > 0 ? `${o.repacks}× repack` : ""}</span>
                    </div>
                    <div className="text-xs text-[#A19D94] mt-1">{o.email}{o.phone ? ` · ${o.phone}` : ""} · ${o.amount / 100}</div>
                    {o.flavor_note && <div className="text-xs text-[#F4D77E] mt-1">Flavor: {o.flavor_note}</div>}
                  </div>
                  <button onClick={() => redeemHookah(o.session_id)} disabled={hookahBusy === o.session_id} data-testid={`hookah-redeem-${o.session_id}`}
                    className={`text-[10px] uppercase tracking-[0.15em] border px-4 py-2 transition-colors disabled:opacity-50 ${
                      o.redeemed ? "border-green-400/40 text-green-300" : "border-[#D4AF37]/50 text-[#F4D77E] hover:bg-[#D4AF37]/10"}`}>
                    {hookahBusy === o.session_id ? "…" : o.redeemed ? "Picked Up ✓" : "Mark Picked Up"}
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === "door" && (
          <div data-testid="section-door">
            <SectionHead icon={ScanLine} title="Door Check-In"
              sub="Scan guest QR codes or enter the code under the QR. One tap checks in one guest." />

            {!isOnline && (
              <div className="border border-amber-400/50 bg-amber-950/20 p-4 mb-6 flex items-center justify-between" data-testid="door-offline-banner">
                <span className="text-amber-300 text-xs uppercase tracking-[0.2em]">Offline mode — checking against the cached door list</span>
                <span className="text-amber-200 text-xs" data-testid="door-queue-count">{doorQueue.length} queued</span>
              </div>
            )}
            {isOnline && doorQueue.length > 0 && (
              <div className="border border-[#D4AF37]/40 bg-[#D4AF37]/5 p-4 mb-6 flex items-center justify-between" data-testid="door-queued-banner">
                <span className="text-[#F4D77E] text-xs uppercase tracking-[0.2em]">{doorQueue.length} check-in{doorQueue.length === 1 ? "" : "s"} queued offline</span>
                <button onClick={syncQueue} data-testid="door-sync-button"
                  className="text-[10px] uppercase tracking-[0.15em] border border-[#D4AF37] text-[#F4D77E] px-4 py-2 hover:bg-[#D4AF37]/10 transition-colors">Sync Now</button>
              </div>
            )}
            <p className="text-[11px] text-[#5c5850] mb-6" data-testid="door-manifest-status">
              Door list: {doorManifest
                ? `${doorManifest.length} guest order${doorManifest.length === 1 ? "" : "s"} cached on this device${doorManifestAt ? ` · synced ${new Date(doorManifestAt).toLocaleString()}` : ""}`
                : "not cached yet — open this tab while online once before doors"}
              {isOnline && (
                <button onClick={refreshManifest} data-testid="door-refresh-manifest"
                  className="ml-3 text-[#8C7324] hover:text-[#D4AF37] uppercase tracking-[0.15em] transition-colors">Refresh</button>
              )}
            </p>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6" data-testid="door-counts">
              {(analytics?.tickets || staffBoot?.tickets || []).map((t) => (
                <div key={t.slug} className="border border-white/10 bg-black/40 p-4" data-testid={`door-count-${t.slug}`}>
                  <div className="text-[10px] uppercase tracking-[0.15em] text-[#A19D94]">{t.title}</div>
                  <div className="font-serif-display text-2xl text-[#F4D77E] mt-1">{t.checked_in ?? 0} <span className="text-sm text-[#5c5850]">/ {t.sold} in</span></div>
                </div>
              ))}
            </div>

            <div className="grid lg:grid-cols-2 gap-6">
              <div className="border border-white/10 bg-black/40 p-6" data-testid="door-scanner-card">
                <span className="text-xs uppercase tracking-[0.25em] text-[#D4AF37]">Scanner</span>
                <div className="relative mt-4 w-full min-h-[260px] border border-dashed border-white/15 flex items-center justify-center">
                  {!scanning && <span className="text-[#5c5850] text-xs uppercase tracking-[0.2em]">Camera off</span>}
                  <div id="door-scanner" className="w-full" data-testid="door-scanner" />
                </div>
                <div className="flex flex-wrap gap-3 mt-4">
                  {!scanning ? (
                    <button onClick={startScanner} data-testid="door-start-button"
                      className="text-xs uppercase tracking-[0.2em] border border-[#D4AF37] bg-[#D4AF37] text-black px-6 py-3 hover:bg-transparent hover:text-[#D4AF37] transition-colors duration-300">
                      Start Camera
                    </button>
                  ) : (
                    <button onClick={stopScanner} data-testid="door-stop-button"
                      className="text-xs uppercase tracking-[0.2em] border border-red-400/50 text-red-300 px-6 py-3 hover:bg-red-400/10 transition-colors">
                      Stop Camera
                    </button>
                  )}
                </div>
                <div className="flex gap-3 mt-5">
                  <input value={manualCode} onChange={(e) => setManualCode(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && lookupCode(manualCode)}
                    placeholder="Or type the code under the QR" data-testid="door-manual-code"
                    className="flex-1 bg-transparent border border-white/20 px-4 py-3 text-sm text-[#FDFBF7] placeholder-[#5c5850] tracking-[0.2em] focus:outline-none focus:border-[#D4AF37] transition-colors" />
                  <button onClick={() => lookupCode(manualCode)} disabled={doorBusy || !manualCode.trim()} data-testid="door-lookup-button"
                    className="text-xs uppercase tracking-[0.2em] border border-white/25 text-[#A19D94] px-5 py-3 hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors disabled:opacity-50">
                    Look Up
                  </button>
                </div>
                <p className="text-[11px] text-[#5c5850] mt-4">Staff flow: guest opens their receipt QR → point this camera (or any phone camera opens the same page) → tap Check In once per person. Screenshots can't be reused — already-scanned parties show their count.</p>
              </div>

              <div className="border border-white/10 bg-black/40 p-6" data-testid="door-result-card">
                <span className="text-xs uppercase tracking-[0.25em] text-[#D4AF37]">Guest</span>
                {doorError && <p className="text-red-400 text-sm mt-4" data-testid="door-error">{doorError}</p>}
                {doorFlash && <p className="text-green-400 text-sm uppercase tracking-[0.2em] mt-4" data-testid="door-flash">{doorFlash}</p>}
                {!doorTicket && !doorError && !doorFlash && (
                  <p className="text-sm text-[#5c5850] mt-4" data-testid="door-idle">Scan or look up a ticket to see the party.</p>
                )}
                {doorTicket && (
                  <div className="mt-4" data-testid="door-ticket-card">
                    {doorTicket.events.map((t) => <div key={t} className="font-serif-display text-2xl text-[#FDFBF7]">{t}</div>)}
                    <div className="text-[11px] uppercase tracking-[0.2em] text-[#A19D94] mt-2">Order {doorTicket.order_ref} · {doorTicket.checked_in_count} of {doorTicket.quantity} in</div>
                    <div className="flex gap-2 mt-3">
                      {Array.from({ length: doorTicket.quantity }).map((_, i) => (
                        <span key={i} className={`w-3 h-3 rounded-full border ${i < doorTicket.checked_in_count ? "bg-[#D4AF37] border-[#D4AF37]" : "border-white/25"}`} />
                      ))}
                    </div>
                    <div className="flex flex-wrap gap-3 mt-6">
                      {doorTicket.quantity - doorTicket.checked_in_count > 0 ? (
                        <button onClick={doorCheckIn} disabled={doorBusy} data-testid="door-checkin-button"
                          className="text-xs uppercase tracking-[0.2em] border border-[#D4AF37] bg-[#D4AF37] text-black px-8 py-4 hover:bg-transparent hover:text-[#D4AF37] transition-colors duration-300 disabled:opacity-50">
                          {doorBusy ? "…" : `Check In Guest (${doorTicket.checked_in_count + 1} of ${doorTicket.quantity})`}
                        </button>
                      ) : (
                        <span className="text-xs uppercase tracking-[0.2em] text-green-300 border border-green-400/40 bg-green-950/20 px-6 py-4" data-testid="door-complete">Whole party in</span>
                      )}
                      <button onClick={nextGuest} data-testid="door-next-button"
                        className="text-xs uppercase tracking-[0.2em] border border-white/25 text-[#A19D94] px-6 py-4 hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors">
                        Next Guest
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {tab === "guests" && (
          <div data-testid="section-guests">
            <SectionHead icon={UserCheck} title="Guest Lookup"
              sub="Find any sold ticket, section reservation, or hookah preorder by name or email." />

            {!isStaff && (
            <div className="border border-[#D4AF37]/30 bg-black/40 p-6 mb-6" data-testid="golden-tickets-card">
              <span className="text-xs uppercase tracking-[0.25em] text-[#D4AF37] block mb-2">Golden Tickets · Chocolate City</span>
              <p className="text-sm text-[#A19D94] mb-5">Send golden tickets when you're ready — either let the system draw 3 random Chocolate City ticket buyers, or hand-pick the buyers yourself. Each winner gets a unique claim code emailed with next steps for the free manicure/pedicure. Previous winners are never picked again.</p>
              <div className="flex gap-3 mb-5" data-testid="golden-mode-toggle">
                <button onClick={() => { setGoldenMode("random"); setGoldenConfirm(false); }} data-testid="golden-mode-random"
                  className={`text-[10px] uppercase tracking-[0.2em] px-4 py-2 border transition-colors ${goldenMode === "random" ? "border-[#D4AF37] bg-[#D4AF37] text-black" : "border-[#D4AF37]/40 text-[#D4AF37] hover:border-[#D4AF37]"}`}>
                  Draw 3 Random
                </button>
                <button onClick={() => { setGoldenMode("pick"); setGoldenConfirm(false); }} data-testid="golden-mode-pick"
                  className={`text-[10px] uppercase tracking-[0.2em] px-4 py-2 border transition-colors ${goldenMode === "pick" ? "border-[#D4AF37] bg-[#D4AF37] text-black" : "border-[#D4AF37]/40 text-[#D4AF37] hover:border-[#D4AF37]"}`}>
                  Hand-Pick Buyers
                </button>
              </div>
              {goldenMode === "pick" && (
                <div className="mb-5 border border-white/10 bg-black/30 p-4" data-testid="golden-picker">
                  <p className="text-[11px] uppercase tracking-[0.2em] text-[#A19D94] mb-3">Eligible ticket buyers — pick up to 3 ({goldenPicks.length} selected)</p>
                  {goldenEligible.length === 0 ? (
                    <p className="text-sm text-[#5c5850]">No eligible buyers yet — everyone who bought a Chocolate City ticket has already won, or no tickets sold yet.</p>
                  ) : (
                    <div className="max-h-56 overflow-y-auto space-y-1.5">
                      {goldenEligible.map((b) => {
                        const checked = goldenPicks.includes(b.email);
                        return (
                          <label key={b.email} className={`flex items-center gap-3 px-3 py-2 cursor-pointer border transition-colors ${checked ? "border-[#D4AF37]/60 bg-[#D4AF37]/10" : "border-transparent hover:border-white/15"}`}
                            data-testid={`golden-pick-${b.email}`}>
                            <input type="checkbox" checked={checked}
                              onChange={() => setGoldenPicks((p) => checked ? p.filter((e) => e !== b.email) : (p.length >= 3 ? p : [...p, b.email]))}
                              className="accent-[#D4AF37] w-4 h-4" />
                            <span className="text-sm text-[#FDFBF7] truncate">{b.name || b.email}</span>
                            {b.name && <span className="text-xs text-[#8C7324] truncate ml-auto">{b.email}</span>}
                          </label>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
              <div className="grid sm:grid-cols-2 gap-4 mb-5">
                <label className="block">
                  <span className="text-[11px] uppercase tracking-[0.2em] text-[#A19D94]">Claim by</span>
                  <input value={goldenForm.claim_deadline} onChange={(e) => setGoldenForm((f) => ({ ...f, claim_deadline: e.target.value }))}
                    className={`${eventInputCls} mt-2`} data-testid="golden-claim-deadline" />
                </label>
                <label className="block">
                  <span className="text-[11px] uppercase tracking-[0.2em] text-[#A19D94]">Appointment by</span>
                  <input value={goldenForm.redemption_deadline} onChange={(e) => setGoldenForm((f) => ({ ...f, redemption_deadline: e.target.value }))}
                    className={`${eventInputCls} mt-2`} data-testid="golden-redemption-deadline" />
                </label>
                <label className="block">
                  <span className="text-[11px] uppercase tracking-[0.2em] text-[#A19D94]">Prize includes</span>
                  <input value={goldenForm.package} onChange={(e) => setGoldenForm((f) => ({ ...f, package: e.target.value }))}
                    className={`${eventInputCls} mt-2`} data-testid="golden-package" />
                </label>
                <label className="block">
                  <span className="text-[11px] uppercase tracking-[0.2em] text-[#A19D94]">Exclusions</span>
                  <input value={goldenForm.exclusions} onChange={(e) => setGoldenForm((f) => ({ ...f, exclusions: e.target.value }))}
                    className={`${eventInputCls} mt-2`} data-testid="golden-exclusions" />
                </label>
              </div>
              {!goldenConfirm ? (
                <button onClick={() => setGoldenConfirm(true)} disabled={goldenBusy || (goldenMode === "pick" && goldenPicks.length === 0)} data-testid="golden-draw-button"
                  className="text-xs uppercase tracking-[0.2em] border border-[#D4AF37] bg-[#D4AF37] text-black px-6 py-3 hover:bg-transparent hover:text-[#D4AF37] transition-colors duration-300 disabled:opacity-50">
                  {goldenMode === "pick" ? `Send Golden Ticket${goldenPicks.length === 1 ? "" : "s"} to ${goldenPicks.length || "…"} Picked` : "Draw 3 Golden Ticket Winners"}
                </button>
              ) : (
                <span className="inline-flex items-center gap-3 border border-red-400/50 bg-red-950/30 px-5 py-3" data-testid="golden-confirm">
                  <span className="text-xs uppercase tracking-[0.15em] text-red-300">
                    {goldenMode === "pick" ? `Email ${goldenPicks.length} hand-picked buyer${goldenPicks.length === 1 ? "" : "s"} their golden ticket now?` : "Pick 3 random buyers and email them now?"}
                  </span>
                  <button onClick={drawGolden} disabled={goldenBusy} data-testid="golden-confirm-send"
                    className="text-xs uppercase tracking-[0.15em] text-[#F4D77E] hover:text-white transition-colors">
                    {goldenBusy ? "Sending…" : "Yes, Send Now"}
                  </button>
                  <button onClick={() => setGoldenConfirm(false)} data-testid="golden-confirm-cancel"
                    className="text-xs uppercase tracking-[0.15em] text-[#A19D94] hover:text-white transition-colors">Cancel</button>
                </span>
              )}
              {goldenList.length > 0 && (
                <div className="mt-6 space-y-2" data-testid="golden-list">
                  {goldenList.map((g) => (
                    <div key={g.code} className="flex flex-wrap items-center justify-between gap-3 border border-white/10 bg-black/30 px-4 py-3" data-testid={`golden-${g.code}`}>
                      <div className="min-w-0">
                        <span className="text-sm text-[#FDFBF7]">{g.name || g.email}</span>
                        <span className="text-[11px] text-[#8C7324] ml-3">{g.code}</span>
                      </div>
                      <span className={`text-[10px] uppercase tracking-[0.18em] border px-3 py-1.5 ${
                        g.status === "claimed" ? "border-green-400/40 text-green-300" : "border-[#D4AF37]/40 text-[#F4D77E]"}`}
                        data-testid={`golden-status-${g.code}`}>
                        {g.status === "claimed" ? "Claimed" : "Sent"}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
            )}

            <div className="border border-white/10 bg-black/40 p-6 mb-6">
              <div className="flex gap-3">
                <input value={guestQuery} onChange={(e) => searchGuests(e.target.value)}
                  placeholder="Type a name or email…" data-testid="guest-search-input"
                  className="flex-1 bg-transparent border border-white/20 px-4 py-3 text-sm text-[#FDFBF7] placeholder-[#5c5850] focus:outline-none focus:border-[#D4AF37] transition-colors" />
              </div>
              {!isStaff && (
                <div className="mt-4 pt-4 border-t border-white/10 flex flex-wrap items-center gap-4" data-testid="qr-tools">
                  {!qrConfirm ? (
                    <button onClick={() => setQrConfirm(true)} data-testid="send-qrs-all-button"
                      className="text-[10px] uppercase tracking-[0.15em] border border-[#D4AF37]/60 text-[#D4AF37] px-4 py-2 hover:bg-[#D4AF37] hover:text-black transition-colors">
                      Send QR Passes To All Ticket Holders
                    </button>
                  ) : (
                    <span className="inline-flex items-center gap-3 border border-red-400/50 bg-red-950/30 px-4 py-2" data-testid="send-qrs-confirm">
                      <span className="text-[10px] uppercase tracking-[0.15em] text-red-300">Email every paid ticket holder their QR pass?</span>
                      <button onClick={sendQrAll} disabled={qrBusy === "all"} data-testid="send-qrs-confirm-yes"
                        className="text-[10px] uppercase tracking-[0.15em] text-[#F4D77E] hover:text-white transition-colors">
                        {qrBusy === "all" ? "Sending…" : "Yes, Send All"}
                      </button>
                      <button onClick={() => setQrConfirm(false)} data-testid="send-qrs-confirm-cancel"
                        className="text-[10px] uppercase tracking-[0.15em] text-[#A19D94] hover:text-white transition-colors">Cancel</button>
                    </span>
                  )}
                </div>
              )}
              {staffBoot && (
                <p className="text-[11px] text-[#5c5850] mt-3" data-testid="guest-quick-stats">
                  {staffBoot.tickets.map((t) => `${t.checked_in}/${t.sold} in (${t.title})`).join(" · ")} · {staffBoot.section_reservations} sections reserved · {staffBoot.hookah_orders} hookah orders
                </p>
              )}
            </div>

            {guestBusy && <p className="text-sm text-[#A19D94]">Searching…</p>}
            {!guestBusy && guestResults !== null && guestResults.length === 0 && (
              <p className="text-sm text-[#A19D94]" data-testid="guest-no-results">No paid orders match that search.</p>
            )}
            <div className="space-y-3">
              {(guestResults || []).map((r, i) => (
                <div key={i} className="border border-white/10 bg-black/40 p-5 flex flex-wrap items-center justify-between gap-4" data-testid={`guest-result-${i}`}>
                  <div>
                    <div className="flex items-center gap-2 text-[#FDFBF7] font-semibold">
                      {r.kind === "ticket" && <TicketIcon className="w-4 h-4 text-[#D4AF37]" />}
                      {r.kind === "section" && <Armchair className="w-4 h-4 text-[#D4AF37]" />}
                      {r.kind === "hookah" && <Wind className="w-4 h-4 text-[#D4AF37]" />}
                      {r.label}
                    </div>
                    <div className="text-xs text-[#A19D94] mt-1">{r.name || "—"} · {r.email}</div>
                    {r.detail && <div className="text-xs text-[#8C7324] mt-1">{r.detail}</div>}
                    {r.ticket_code && <div className="text-[10px] uppercase tracking-[0.2em] text-[#5c5850] mt-1">Code {r.ticket_code}</div>}
                  </div>
                  <div className="flex items-center gap-3 shrink-0">
                    {!isStaff && r.kind === "ticket" && r.session_id && (
                      <button onClick={() => sendQrOne(r.session_id)} disabled={qrBusy === r.session_id} data-testid={`send-qr-${i}`}
                        className="text-[10px] uppercase tracking-[0.15em] border border-[#D4AF37]/60 text-[#D4AF37] px-4 py-2 hover:bg-[#D4AF37] hover:text-black transition-colors disabled:opacity-50">
                        {qrBusy === r.session_id ? "…" : r.qr_sent_at ? "Resend QR" : "Send QR"}
                      </button>
                    )}
                    <span className={`text-[10px] uppercase tracking-[0.15em] border px-3 py-1.5 ${
                      (r.status === "Settled" || r.status === "Picked up" || (r.kind === "ticket" && r.status && !r.status.startsWith("0 ")))
                        ? "border-[#D4AF37]/50 text-[#F4D77E]"
                        : "border-white/15 text-[#A19D94]"}`} data-testid={`guest-status-${i}`}>
                      {r.status}
                    </span>
                  </div>
                </div>
              ))}
            </div>

            {!isStaff && (
              <div className="mt-8" data-testid="tickets-list">
                <div className="text-xs uppercase tracking-[0.25em] text-[#D4AF37] mb-3">
                  Ticket Purchases ({ticketOrders.length})
                </div>
                {ticketOrders.length === 0 ? (
                  <p className="text-sm text-[#A19D94]" data-testid="tickets-empty">No paid ticket orders yet.</p>
                ) : (
                  <div className="space-y-3">
                    {ticketOrders.map((t) => (
                      <div key={t.session_id} className="border border-white/10 bg-black/40 p-5 flex flex-wrap items-center gap-5" data-testid={`ticket-row-${t.session_id}`}>
                        {t.qr_url && (
                          <a href={`${API}${t.qr_url}`} target="_blank" rel="noreferrer" data-testid={`ticket-qr-link-${t.session_id}`}>
                            <img src={`${API}${t.qr_url}`} alt={`QR ${t.ticket_code}`}
                              className="w-16 h-16 border-2 border-white bg-white" data-testid={`ticket-qr-${t.session_id}`} />
                          </a>
                        )}
                        <div className="flex-1 min-w-[220px]">
                          <div className="flex flex-wrap items-center gap-2 text-[#FDFBF7] font-semibold">
                            {t.name || "—"}
                            {t.comp && <span className="text-[9px] uppercase tracking-[0.2em] border border-[#D4AF37] text-[#F4D77E] px-2 py-0.5">Comp</span>}
                          </div>
                          <div className="text-xs text-[#A19D94] mt-1">{t.email}</div>
                          <div className="text-xs text-[#8C7324] mt-1">
                            {t.product_label} · {t.quantity} ticket{t.quantity > 1 ? "s" : ""} · ${(t.amount / 100).toFixed(2)} ·
                            <span className="text-[#F4D77E]"> {t.ticket_code}</span> · {t.checked_in}/{t.quantity} in
                            {t.qr_sent_at ? " · QR sent" : ""}
                          </div>
                        </div>
                        <button onClick={() => sendQrOne(t.session_id)} disabled={qrBusy === t.session_id} data-testid={`ticket-send-qr-${t.session_id}`}
                          className="shrink-0 text-[10px] uppercase tracking-[0.15em] border border-[#D4AF37]/60 text-[#D4AF37] px-4 py-2 hover:bg-[#D4AF37] hover:text-black transition-colors disabled:opacity-50">
                          {qrBusy === t.session_id ? "…" : t.qr_sent_at ? "Resend QR" : "Send QR"}
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {tab === "customers" && (
          <div data-testid="section-customers">
            <SectionHead icon={Search} title="Customer Lookup" sub="Find anyone by name, email, or phone — then manage their codes and reservation details." />
            <form onSubmit={searchCustomers} className="flex gap-3 mb-8 max-w-xl">
              <input value={custQ} onChange={(e) => setCustQ(e.target.value)} data-testid="customer-search-input"
                placeholder="Name, email, or phone number…"
                className="flex-1 bg-black/50 border border-white/20 px-5 py-3.5 text-[#FDFBF7] placeholder-[#5c5850] focus:outline-none focus:border-[#D4AF37] transition-colors" />
              <button type="submit" disabled={custBusy} data-testid="customer-search-button"
                className="text-xs uppercase tracking-[0.2em] border border-[#D4AF37] bg-[#D4AF37] text-black px-6 py-3 hover:bg-transparent hover:text-[#D4AF37] transition-colors duration-300 disabled:opacity-50">
                {custBusy ? "Searching…" : "Search"}
              </button>
            </form>

            {!custProfile && custResults.length > 0 && (
              <div className="space-y-3 max-w-3xl" data-testid="customer-results">
                {custResults.map((c, i) => (
                  <button key={c.email} onClick={() => openCustomer(c.email)} data-testid={`customer-result-${i}`}
                    className="w-full text-left border border-white/10 bg-black/40 p-5 hover:border-[#D4AF37]/50 transition-colors">
                    <div className="flex flex-wrap items-center justify-between gap-3">
                      <div>
                        <div className="text-[#FDFBF7] font-semibold">{c.name || "—"}</div>
                        <div className="text-xs text-[#A19D94] mt-1">{c.email}{c.phone ? ` · ${c.phone}` : ""}</div>
                        <div className="text-[10px] uppercase tracking-[0.15em] text-[#8C7324] mt-2">{c.sources.join(" · ") || "on file"}</div>
                      </div>
                      {c.early_code && (
                        <span className={`text-[10px] uppercase tracking-[0.15em] border px-3 py-1.5 ${c.code_active && !c.code_used ? "border-[#D4AF37]/50 text-[#F4D77E]" : "border-white/15 text-[#A19D94]"}`}>
                          {c.early_code} · {c.code_used ? "used" : c.code_active ? "active" : "inactive"}
                        </span>
                      )}
                    </div>
                  </button>
                ))}
              </div>
            )}

            {custProfileBusy && <p className="text-sm text-[#A19D94]">Loading profile…</p>}

            {custProfile && !custProfileBusy && (
              <div className="max-w-3xl" data-testid="customer-profile">
                <button onClick={() => setCustProfile(null)} data-testid="customer-back"
                  className="text-[10px] uppercase tracking-[0.2em] text-[#A19D94] hover:text-[#D4AF37] mb-5 transition-colors">← Back to results</button>

                <div className="border border-white/10 bg-black/40 p-6 mb-5">
                  <span className="text-xs uppercase tracking-[0.25em] text-[#D4AF37] block mb-4">Profile</span>
                  <div className="text-[#FDFBF7] font-semibold" data-testid="customer-email">{custProfile.email}</div>
                  <div className="grid sm:grid-cols-2 gap-3 mt-4">
                    <input value={custEdit.name} onChange={(e) => setCustEdit((f) => ({ ...f, name: e.target.value }))}
                      placeholder="Name" data-testid="customer-edit-name"
                      className="bg-black/50 border border-white/20 px-4 py-3 text-sm text-[#FDFBF7] placeholder-[#5c5850] focus:outline-none focus:border-[#D4AF37]" />
                    <input value={custEdit.phone} onChange={(e) => setCustEdit((f) => ({ ...f, phone: e.target.value }))}
                      placeholder="Phone" data-testid="customer-edit-phone"
                      className="bg-black/50 border border-white/20 px-4 py-3 text-sm text-[#FDFBF7] placeholder-[#5c5850] focus:outline-none focus:border-[#D4AF37]" />
                  </div>
                  <button onClick={() => saveCustomerProfile(custProfile.email)} data-testid="customer-save-profile"
                    className="mt-4 text-[10px] uppercase tracking-[0.2em] border border-[#D4AF37]/60 text-[#D4AF37] px-5 py-2.5 hover:bg-[#D4AF37] hover:text-black transition-colors">
                    Save Name & Phone
                  </button>
                </div>

                <div className="border border-[#D4AF37]/25 bg-black/40 p-6 mb-5" data-testid="customer-code-card">
                  <span className="text-xs uppercase tracking-[0.25em] text-[#D4AF37] block mb-4">Early Access Code</span>
                  {custProfile.early_access?.code ? (
                    <>
                      <div className="flex flex-wrap items-center gap-3 mb-3">
                        <span className="font-mono text-lg tracking-[0.2em] text-[#F4D77E]" data-testid="customer-code">{custProfile.early_access.code}</span>
                        <span className={`text-[10px] uppercase tracking-[0.15em] border px-3 py-1.5 ${custProfile.early_access.code_active !== false && !custProfile.early_access.code_used ? "border-[#D4AF37]/50 text-[#F4D77E]" : "border-red-400/40 text-red-300"}`} data-testid="customer-code-status">
                          {custProfile.early_access.code_used ? "Already used" : custProfile.early_access.code_active === false ? "Inactive" : "Active"}
                        </span>
                      </div>
                      {custProfile.promo && (
                        <p className="text-xs text-[#A19D94] mb-3">
                          Carries a {custProfile.promo.percent_off}% discount · {custProfile.promo.active ? "promo active" : "promo inactive"} · redeemed {custProfile.promo.times_redeemed}/{custProfile.promo.max_redemptions || "∞"}
                        </p>
                      )}
                      <div className="flex flex-wrap gap-3">
                        <button onClick={() => toggleCustomerCode(custProfile.email, custProfile.early_access.code_active === false)} data-testid="customer-code-toggle"
                          className="text-[10px] uppercase tracking-[0.2em] border border-white/25 text-[#A19D94] px-5 py-2.5 hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors">
                          {custProfile.early_access.code_active === false ? "Reactivate Code" : "Deactivate Code"}
                        </button>
                        <button onClick={() => regenCustomerCode(custProfile.email)} data-testid="customer-code-regen"
                          className="text-[10px] uppercase tracking-[0.2em] border border-[#D4AF37] bg-[#D4AF37] text-black px-5 py-2.5 hover:bg-transparent hover:text-[#D4AF37] transition-colors">
                          Regenerate & Email New Code
                        </button>
                      </div>
                      <p className="text-[10px] text-[#5c5850] mt-3">Regenerating kills the old code, mints a fresh one (same discount if it had one) and emails it to them.</p>
                    </>
                  ) : (
                    <p className="text-sm text-[#A19D94]" data-testid="customer-no-code">No early access code on file — send codes from the Early Access tab.</p>
                  )}
                </div>

                {custProfile.reservations.length > 0 && (
                  <div className="border border-white/10 bg-black/40 p-6 mb-5" data-testid="customer-reservations">
                    <span className="text-xs uppercase tracking-[0.25em] text-[#D4AF37] block mb-4">Section Reservations</span>
                    <div className="space-y-3">
                      {custProfile.reservations.map((r, i) => (
                        <div key={r.session_id} className="border border-white/10 p-4" data-testid={`customer-reservation-${i}`}>
                          <div className="flex flex-wrap items-center justify-between gap-2">
                            <span className="text-sm text-[#FDFBF7]">{r.product?.replace("section:", "")} · ${((r.amount || 0) / 100).toFixed(0)}</span>
                            <span className={`text-[10px] uppercase tracking-[0.15em] ${r.payment_status === "paid" ? "text-[#F4D77E]" : "text-[#A19D94]"}`}>{r.payment_status}</span>
                          </div>
                          <div className="text-xs text-[#A19D94] mt-1">
                            {r.event && <>{r.event} · </>}order {r.session_id.slice(0, 14)}…
                            {r.balance_due ? ` · balance $${((r.balance_due || 0) / 100).toFixed(0)}${r.balance_paid || r.settled_offline ? " (settled)" : " due"}` : ""}
                            {r.pay_method ? ` · paid via ${r.pay_method}` : ""}
                          </div>
                          {r.payer_note && <div className="text-[10px] text-[#5c5850] mt-1">Note: {r.payer_note}</div>}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {custProfile.tickets.length > 0 && (
                  <div className="border border-white/10 bg-black/40 p-6 mb-5" data-testid="customer-tickets">
                    <span className="text-xs uppercase tracking-[0.25em] text-[#D4AF37] block mb-4">Tickets</span>
                    <div className="space-y-2">
                      {custProfile.tickets.map((t, i) => (
                        <div key={t.session_id} className="flex flex-wrap justify-between gap-2 text-sm border border-white/10 p-3" data-testid={`customer-ticket-${i}`}>
                          <span className="text-[#FDFBF7]">{t.quantity}× {t.product}</span>
                          <span className="text-[#A19D94]">${((t.amount || 0) / 100).toFixed(0)}{t.ticket_code ? ` · ${t.ticket_code}` : ""}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {custProfile.golden.length > 0 && (
                  <div className="border border-[#D4AF37]/25 bg-black/40 p-6" data-testid="customer-golden">
                    <span className="text-xs uppercase tracking-[0.25em] text-[#D4AF37] block mb-3">Golden Ticket</span>
                    {custProfile.golden.map((g, i) => (
                      <div key={i} className="text-sm text-[#F4D77E]" data-testid={`customer-golden-${i}`}>{g.code} · {g.status}</div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        </main>
      </div>

      {blastHtml && (        <div className="fixed inset-0 z-[80] bg-black/80 backdrop-blur-md flex items-center justify-center p-4 md:p-10" data-testid="blast-preview-modal"
          onClick={() => setBlastHtml(null)}>
          <div className="relative w-full max-w-2xl max-h-[85vh] overflow-y-auto bg-white" onClick={(e) => e.stopPropagation()}>
            <div className="sticky top-0 bg-[#0A0A0A] px-5 py-3 flex items-center justify-between">
              <span className="text-xs uppercase tracking-[0.2em] text-[#D4AF37]">Email preview · {blastCount} recipient{blastCount === 1 ? "" : "s"}</span>
              <button onClick={() => setBlastHtml(null)} data-testid="blast-preview-close" className="text-[#A19D94] hover:text-white text-sm px-2">✕</button>
            </div>
            <div dangerouslySetInnerHTML={{ __html: blastHtml }} />
          </div>
        </div>
      )}
    </div>
  );
}
